import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./HeroSection.css";
import ModernDatePicker from "./ModernDatePicker";
import LoginModal from "./LoginModal";
import {
  FaCalendarAlt,
  FaClock,
  FaTaxi,
  FaPlus,
  FaPlane,
  FaArrowRight,
  FaExchangeAlt,
  FaChevronDown,
  FaTimes,
  FaSearch,
  FaInfoCircle,
  FaArrowLeft,
  FaUser,
  FaCheck,
} from "react-icons/fa";
import LocationInput from "./LocationInput";
import { stripPlusCode } from "../utils/locationMatcher";
import AirportSelector from "./AirportSelector";
import CitySelector from "./CitySelector";
import { updateBookingForm } from "../features/booking/bookingSlice";
import { API_URL, FALLBACK_API_URL } from "../config/apiConfig";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import { getCorporateMember } from "../utils/corporateSession";

const KNOWN_DISTANCES = {
  "rajkot-delhi": 1140,
  "delhi-rajkot": 1140,
  "mumbai-pune": 150,
  "pune-mumbai": 150,
  "bangalore-chennai": 350,
  "chennai-bangalore": 350,
};

const normalizeCityName = (city) => {
  if (!city) return "";
  return city.toString().toLowerCase().trim();
};

const getKnownDistance = (from, to) => {
  const fromNormalized = normalizeCityName(from);
  const toNormalized = normalizeCityName(to);
  const cacheKey = `${fromNormalized}-${toNormalized}`;
  return KNOWN_DISTANCES[cacheKey] || null;
};

const HeroSection = ({
  journeyType,
  setJourneyType,
  tripType,
  setTripType,
  localServiceType,
  setLocalServiceType,
  pickup,
  setPickup,
  dropoff,
  setDropoff,
  pickupPlace,
  setPickupPlace,
  dropoffPlace,
  setDropoffPlace,
  localCity,
  setLocalCity,
  hourlyPackage,
  setHourlyPackage,
  selectedAirport,
  setSelectedAirport,
  transferDirection,
  setTransferDirection,
  airportCity,
  setAirportCity,
  airportServiceCities,
  pickupDate,
  setPickupDate,
  pickupTime,
  setPickupTime,
  returnDate,
  setReturnDate,
  returnTime,
  setReturnTime,

  multiCity,
  setMultiCity,
  additionalCities,
  setAdditionalCities,
  cityCounter,
  setCityCounter,
  rentalPackages,
  airports,
  errorMessage,
  setErrorMessage,
  isLoading,
  setIsLoading,
  handleSubmit,
  convertTo24Hour,
  convertTo12Hour,
  getCurrentTime,
  isToday,
  validateTime,
  getEarliestAllowedTime,
  handlePickupTimeChange,
  handleAirportLocationSelected,
}) => {
  const [isEditingFrom, setIsEditingFrom] = useState(false);
  const [isEditingTo, setIsEditingTo] = useState(false);
  const [isEditingAirport, setIsEditingAirport] = useState(false);
  const [isEditingCity, setIsEditingCity] = useState(false);
  const [isEditingPackage, setIsEditingPackage] = useState(false);
  const [openPackageSelector, setOpenPackageSelector] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(null); // 'departure', 'return', null
  const [datePickerAnchorRect, setDatePickerAnchorRect] = useState(null);
  const [datePickerSize, setDatePickerSize] = useState({ width: 480, height: 250 });
  const [openTimePicker, setOpenTimePicker] = useState(null); // 'pickup', 'drop', null
  const { user } = useSelector((state) => state.auth);
  const corporateMember = getCorporateMember();
  const [timePickerAnchorRect, setTimePickerAnchorRect] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    const pendingSubmit = localStorage.getItem("pendingSubmitAfterLogin") === "true";
    if (pendingSubmit) {
      console.log("🔄 Triggering pending booking form submit inside HeroSection...");
      localStorage.removeItem("pendingSubmitAfterLogin");
      setTimeout(() => {
        handleSubmit({ preventDefault: () => {} });
      }, 300);
    }
  };
  const [isBookingForModalOpen, setIsBookingForModalOpen] = useState(false);
  const [mobileDateTimeSheet, setMobileDateTimeSheet] = useState({ isOpen: false, type: 'start' });
  const [showPackageSelector, setShowPackageSelector] = useState(false);

  const TripModeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 4l4 4m0 0l-4 4m4-4H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 20l-4-4m0 0l4-4m-4 4h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const FromIcon = () => (
    <svg width="18" height="8" viewBox="0 0 40 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="4" r="4" fill="#999" />
      <line x1="8" y1="4" x2="32" y2="4" stroke="#e0e0e0" strokeWidth="2" />
      <circle cx="36" cy="4" r="3" stroke="#999" strokeWidth="2" fill="white" />
    </svg>
  );

  const ToIcon = () => (
    <svg width="18" height="8" viewBox="0 0 40 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4" cy="4" r="3" stroke="#999" strokeWidth="2" fill="white" />
      <line x1="8" y1="4" x2="32" y2="4" stroke="#e0e0e0" strokeWidth="2" />
      <circle cx="36" cy="4" r="4" fill="#999" />
    </svg>
  );

  const CalendarIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#999" strokeWidth="2" />
      <path d="M3 10h18M8 2v4M16 2v4" stroke="#999" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
  const handleMobileDateTimeDone = ({ date, hour, minute, period, type, localTripType }) => {
    const timeStr = `${hour}:${minute} ${period}`;
    
    const parseTimeToMinutes = (tStr) => {
      const match = tStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!match) return 0;
      let h = parseInt(match[1]);
      const m = parseInt(match[2]);
      const p = match[3].toUpperCase();
      if (p === 'PM' && h !== 12) h += 12;
      if (p === 'AM' && h === 12) h = 0;
      return h * 60 + m;
    };

    if (type === 'start') {
      setPickupDate(date);

      // Validate: if today, don't allow past pickup time
      let finalTimeStr = timeStr;
      const today = new Date();
      const isDateToday = (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
      if (isDateToday) {
        const selectedMins = parseTimeToMinutes(timeStr);
        const currentMins = today.getHours() * 60 + today.getMinutes();
        if (selectedMins < currentMins) {
          const nowH = today.getHours();
          const nowM = today.getMinutes();
          const nowAmpm = nowH >= 12 ? 'PM' : 'AM';
          const nowDisplayH = nowH % 12 || 12;
          finalTimeStr = `${nowDisplayH}:${String(nowM).padStart(2, '0')} ${nowAmpm}`;
          toast.warn('⏰ Cannot select a past time. Time adjusted to current time.', { autoClose: 3500 });
        }
      }
      setPickupTime(finalTimeStr);
      
      // If dates match, ensure return time is after pickup time
      if (returnDate && date.toDateString() === returnDate.toDateString()) {
        const startMins = parseTimeToMinutes(finalTimeStr);
        const endMins = parseTimeToMinutes(returnTime || "10:00 AM");
        
        if (startMins >= endMins) {
          // Set return to 1 hour after pickup
          let newEndMins = (startMins + 60) % 1440;
          let h = Math.floor(newEndMins / 60);
          const m = newEndMins % 60;
          const p = h >= 12 ? "PM" : "AM";
          h = h % 12 || 12;
          setReturnTime(`${h}:${m.toString().padStart(2, '0')} ${p}`);
        }
      } else if (returnDate && date > returnDate) {
        // If pickup date is moved after return date, update return date
        setReturnDate(date);
        // And ensure time is also valid if it was same day
        const startMins = parseTimeToMinutes(timeStr);
        const endMins = parseTimeToMinutes(returnTime || "10:00 AM");
        if (startMins >= endMins) {
           let newEndMins = (startMins + 60) % 1440;
           let h = Math.floor(newEndMins / 60);
           const m = newEndMins % 60;
           const p = h >= 12 ? "PM" : "AM";
           h = h % 12 || 12;
           setReturnTime(`${h}:${m.toString().padStart(2, '0')} ${p}`);
        }
      }
    } else {
      // Validate return time if same day
      if (pickupDate && date.toDateString() === pickupDate.toDateString()) {
        const startMins = parseTimeToMinutes(pickupTime || "10:00 AM");
        const endMins = parseTimeToMinutes(timeStr);
        
        if (endMins <= startMins) {
          // Don't update or set to valid time
          let newEndMins = (startMins + 60) % 1440;
          let h = Math.floor(newEndMins / 60);
          const m = newEndMins % 60;
          const p = h >= 12 ? "PM" : "AM";
          h = h % 12 || 12;
          setReturnDate(date);
          setReturnTime(`${h}:${m.toString().padStart(2, '0')} ${p}`);
        } else {
          setReturnDate(date);
          setReturnTime(timeStr);
        }
      } else {
        setReturnDate(date);
        setReturnTime(timeStr);
      }
    }

    if (localTripType) {
      setTripType(localTripType);
    }
    setMobileDateTimeSheet({ isOpen: false, type: 'start' });
  };
  const [bookingForAnchorRect, setBookingForAnchorRect] = useState(null);
  const [selectedBookingFor, setSelectedBookingFor] = useState(() => {
    if (typeof window !== "undefined") {
      const bookingFor = sessionStorage.getItem("corporateBookingFor");
      if (bookingFor === 'staff') {
        return {
          fullName: sessionStorage.getItem("selectedStaffName"),
          email: sessionStorage.getItem("selectedStaffEmail"),
          profileImageUrl: sessionStorage.getItem("selectedStaffProfileImageUrl") || null,
          isGuest: false
        };
      } else if (bookingFor === 'guest') {
        return {
          fullName: "Guest",
          email: "External Guest",
          isGuest: true
        };
      }
    }
    return null;
  });
  const [colleagueSearchEmail, setColleagueSearchEmail] = useState("");
  const [colleaguesList, setColleaguesList] = useState([]);
  const [initialStaffList, setInitialStaffList] = useState([]);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('corporateToken');
      if (!token) return;

      const response = await fetch(`${API_URL}/corporate/staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const resData = await response.json();
        setInitialStaffList(resData.data || []);
      }
    } catch (error) {
      // silently fail
    }
  };

  useEffect(() => {
    if (corporateMember) {
      fetchStaffList();
    }
  }, [corporateMember]);

  useEffect(() => {
    if (isBookingForModalOpen) {
      fetchStaffList();
    }
  }, [isBookingForModalOpen]);

  const renderBookingForModalContent = () => {
    const query = colleagueSearchEmail.toLowerCase().trim();
    const filteredStaff = initialStaffList.filter(colleague => {
      if (!query) return true;
      const name = (colleague.fullName || colleague.name || "").toLowerCase();
      const email = (colleague.email || "").toLowerCase();
      const mobile = (colleague.mobileNumber || "").toLowerCase();
      return name.includes(query) || email.includes(query) || mobile.includes(query);
    });

    return (
      <div className="flex flex-col h-full bg-white">
        {/* Search Input */}
        <div className="relative px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <span className="absolute left-8 top-1/2 -translate-y-1/2 text-gray-400">
            <FaSearch size={14} />
          </span>
          <input
            type="text"
            value={colleagueSearchEmail}
            onChange={(e) => setColleagueSearchEmail(e.target.value)}
            placeholder="Search by name or mobile..."
            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-[14px] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#F56753] focus:border-[#F56753]"
          />
        </div>

        {/* Scrollable list content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar max-h-[300px]">
          {/* Yourself / Myself option */}
          {(!query || "myself".includes(query) || (corporateMember?.fullName || corporateMember?.name || "").toLowerCase().includes(query)) && (
            <button
              type="button"
              onClick={() => {
                setSelectedBookingFor(null);
                sessionStorage.setItem("corporateBookingFor", "myself");
                sessionStorage.removeItem("selectedStaffId");
                sessionStorage.removeItem("selectedStaffName");
                sessionStorage.removeItem("selectedStaffMobile");
                sessionStorage.removeItem("selectedStaffEmail");
                setIsBookingForModalOpen(false);
              }}
              className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-all ${
                !selectedBookingFor
                  ? 'border-red-100 bg-red-50/20'
                  : 'border-transparent bg-white hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-700 flex-shrink-0">
                  <FaUser size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-gray-800 text-[15px]">Myself</div>
                  <div className="text-xs text-gray-400 mt-0.5">Book for yourself</div>
                </div>
              </div>
              {!selectedBookingFor && (
                <div className="w-6 h-6 rounded-full bg-[#F56753] flex items-center justify-center text-white flex-shrink-0">
                  <FaCheck size={10} />
                </div>
              )}
            </button>
          )}

          {/* Company Staff Section */}
          {filteredStaff.length > 0 && (
            <div className="space-y-2">
              {filteredStaff.map((colleague, index) => {
                const isSelected = selectedBookingFor !== null && !selectedBookingFor.isGuest && selectedBookingFor.email === colleague.email;
                return (
                  <button
                    key={colleague._id || index}
                    type="button"
                    onClick={() => {
                      setSelectedBookingFor({
                        fullName: colleague.fullName || colleague.name,
                        email: colleague.email,
                        profileImageUrl: colleague.profileImageUrl || null,
                        isGuest: false
                      });
                      sessionStorage.setItem("corporateBookingFor", "staff");
                      sessionStorage.setItem("selectedStaffId", colleague._id);
                      sessionStorage.setItem("selectedStaffName", colleague.fullName || colleague.name);
                      sessionStorage.setItem("selectedStaffMobile", colleague.mobileNumber || colleague.phone || "");
                      sessionStorage.setItem("selectedStaffEmail", colleague.email || "");
                      sessionStorage.setItem("selectedStaffProfileImageUrl", colleague.profileImageUrl || "");
                      setIsBookingForModalOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-all ${
                      isSelected
                        ? 'border-red-100 bg-red-50/20'
                        : 'border-transparent bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {colleague.profileImageUrl ? (
                        <img
                          src={colleague.profileImageUrl}
                          alt={colleague.fullName || colleague.name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(colleague.fullName || colleague.name || 'Staff')}&background=FCA5A5&color=F56753`;
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#F56753]/10 flex items-center justify-center text-[#F56753] font-bold text-sm flex-shrink-0">
                          {(colleague.fullName || colleague.name || "S").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-gray-800 text-[15px]">{colleague.fullName || colleague.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{colleague.mobileNumber || colleague.phone || colleague.email}</div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-[#F56753] flex items-center justify-center text-white flex-shrink-0">
                        <FaCheck size={10} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Guest Option */}
          {(!query || "guest".includes(query) || "other".includes(query)) && (
            <button
              type="button"
              onClick={() => {
                setSelectedBookingFor({ fullName: "Guest", email: "External Guest", isGuest: true });
                sessionStorage.setItem("corporateBookingFor", "guest");
                sessionStorage.removeItem("selectedStaffId");
                sessionStorage.removeItem("selectedStaffName");
                sessionStorage.removeItem("selectedStaffMobile");
                sessionStorage.removeItem("selectedStaffEmail");
                setIsBookingForModalOpen(false);
              }}
              className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-all ${
                selectedBookingFor?.isGuest
                  ? 'border-red-100 bg-red-50/20'
                  : 'border-transparent bg-white hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">
                  <FaUser size={16} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-gray-800 text-[15px]">Guest (Other)</div>
                  <div className="text-xs text-gray-400 mt-0.5">Book for a walk-in guest</div>
                </div>
              </div>
              {selectedBookingFor?.isGuest && (
                <div className="w-6 h-6 rounded-full bg-[#F56753] flex items-center justify-center text-white flex-shrink-0">
                  <FaCheck size={10} />
                </div>
              )}
            </button>
          )}

          {/* No results placeholder */}
          {filteredStaff.length === 0 && query && (
            <div className="text-center py-6 text-gray-500 text-sm">
              No staff found matching "{colleagueSearchEmail}"
            </div>
          )}
        </div>
      </div>
    );
  };

  const [packageSelectorAnchorRect, setPackageSelectorAnchorRect] = useState(null);
  const [packageSearchTerm, setPackageSearchTerm] = useState('');
  const packageSearchInputRef = useRef(null);



  const [mobileSearchModal, setMobileSearchModal] = useState({ isOpen: false, type: 'from' });
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const stored = localStorage.getItem('recentLocationSearches');
      return stored ? JSON.parse(stored) : [
        { name: 'Rajkot, Gujarat, India', description: 'Rajkot, Gujarat, India' },
        { name: 'Dehradun, Uttarakhand, India', description: 'Dehradun, Uttarakhand, India' }
      ];
    } catch (e) {
      return [];
    }
  });
  const [tempPickup, setTempPickup] = useState(pickup);
  const [tempDropoff, setTempDropoff] = useState(dropoff);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSuggestions, setMobileSuggestions] = useState([]);

  const mobileFromRef = useRef(null);
  const mobileToRef = useRef(null);
  const mobileAirportCityRef = useRef(null);
  const mobileStopRef = useRef(null);

  const handleMobileSearchOpen = (type) => {
    setMobileSearchModal({ isOpen: true, type });
    setTempPickup(pickup);
    setTempDropoff(dropoff);
    setSearchQuery("");
    setMobileSuggestions([]);
    try {
      const stored = localStorage.getItem('recentLocationSearches');
      if (stored) setRecentSearches(JSON.parse(stored));
    } catch (e) { /* ignore */ }
  };

  const handleMobileSearchClose = () => {
    setMobileSearchModal({ isOpen: false, type: 'from' });
    setMobileSuggestions([]);
    setSearchQuery("");
  };

  const [heroBgImage, setHeroBgImage] = useState(() => {
    return localStorage.getItem('heroBgImage') || "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&h=900&fit=crop";
  });

  useEffect(() => {
    const fetchHeroImage = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'web_hero' }
        });
        if (response.data.success && response.data.data.length > 0) {
          const newImageUrl = response.data.data[0].imageUrl;
          setHeroBgImage(newImageUrl);
          localStorage.setItem('heroBgImage', newImageUrl);
        }
      } catch (error) {
        console.error("Error fetching hero background image:", error);
      }
    };

    fetchHeroImage();

    // Show login modal on page load/refresh (MMT style) — only once per session
    const alreadyShown = sessionStorage.getItem("loginModalShown");
    if (!alreadyShown) {
      const timer = setTimeout(() => {
        const corporateToken = localStorage.getItem("corporateToken");
        if (!user && !corporateToken) {
          setIsLoginModalOpen(true);
          sessionStorage.setItem("loginModalShown", "true");
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Search colleagues by email
  const searchColleagues = async (email) => {
    if (!email || email.length < 3) {
      setColleaguesList([]);
      return;
    }
    try {
      const token = localStorage.getItem('corporateToken');
      const response = await fetch(`${API_URL}/corporate/members/search?email=${encodeURIComponent(email)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setColleaguesList(data.members || []);
      }
    } catch (error) {
      console.error("Search colleagues error:", error);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchColleagues(colleagueSearchEmail);
    }, 300);
    return () => clearTimeout(timer);
  }, [colleagueSearchEmail]);

  const timePickerRef = useRef(null);
  const timePickerPortalRef = useRef(null);
  const datePickerPortalRef = useRef(null);
  const timePickerAnchorRef = useRef(null);
  const [selectedTime, setSelectedTime] = useState(new Date());

  const getValidInitialTime = (timeStr, date) => {
    const now = new Date();
    const isToday = date && new Date(date).toDateString() === now.toDateString();
    
    if (timeStr) {
      const [time, period] = timeStr.split(' ');
      const [h, m] = time.split(':');
      let hours = parseInt(h);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      const newDate = new Date();
      newDate.setHours(hours, parseInt(m), 0);
      
      if (isToday && newDate < now) {
        let currentH = now.getHours();
        let currentM = Math.ceil(now.getMinutes() / 5) * 5;
        if (currentM >= 60) {
          currentM = 0;
          currentH = (currentH + 1) % 24;
        }
        const validDate = new Date();
        validDate.setHours(currentH, currentM, 0, 0);
        return validDate;
      }
      return newDate;
    }
    
    let currentH = now.getHours();
    let currentM = Math.ceil(now.getMinutes() / 5) * 5;
    if (currentM >= 60) {
      currentM = 0;
      currentH = (currentH + 1) % 24;
    }
    const validDate = new Date();
    validDate.setHours(currentH, currentM, 0);
    return validDate;
  };

  const nowTime = new Date();
  const currentHour24 = nowTime.getHours();
  const currentMinute = nowTime.getMinutes();
  const isTimeToday = Boolean(
    openTimePicker && (
      (openTimePicker.startsWith('pickup') && pickupDate && new Date(pickupDate).toDateString() === new Date().toDateString()) ||
      (openTimePicker === 'drop' && returnDate && new Date(returnDate).toDateString() === new Date().toDateString())
    )
  );
  const isAMDisabled = isTimeToday && currentHour24 >= 12;

  const ensureFutureTime = (dateObj) => {
    const now = new Date();
    if (isTimeToday && dateObj < now) {
      let currentH = now.getHours();
      let currentM = Math.ceil(now.getMinutes() / 5) * 5;
      if (currentM >= 60) {
        currentM = 0;
        currentH = (currentH + 1) % 24;
      }
      const validDate = new Date(dateObj);
      validDate.setHours(currentH, currentM, 0, 0);
      return validDate;
    }
    return dateObj;
  };

  const getHour24 = (h, isPM) => {
    if (isPM) {
      return h === 12 ? 12 : h + 12;
    } else {
      return h === 12 ? 0 : h;
    }
  };

  const isHourDisabled = (h) => {
    if (!isTimeToday) return false;
    const isPM = selectedTime.getHours() >= 12;
    const h24 = getHour24(h, isPM);
    if (h24 < currentHour24) return true;
    if (h24 === currentHour24 && currentMinute > 55) return true;
    return false;
  };

  const isMinuteDisabled = (m) => {
    if (!isTimeToday) return false;
    const selectedHour24 = selectedTime.getHours();
    if (selectedHour24 < currentHour24) return true;
    if (selectedHour24 === currentHour24) {
      return m < currentMinute;
    }
    return false;
  };

  const getTimePickerPosition = useCallback(() => {
    if (!timePickerAnchorRect) return { top: 0, left: 0 };

    const padding = 12;
    const viewportW = window.innerWidth || 0;
    const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const pickerW = 280;

    let left = timePickerAnchorRect.left + scrollX;
    if (timePickerAnchorRect.left + pickerW > viewportW - padding) {
      left = timePickerAnchorRect.right - pickerW + scrollX;
    }
    const maxLeft = viewportW - pickerW - padding + scrollX;
    const minLeft = padding + scrollX;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // Open directly below input in document coordinates (MakeMyTrip style)
    const top = timePickerAnchorRect.bottom + scrollY + 8;

    return { top, left };
  }, [timePickerAnchorRect]);

  const getBookingForModalPosition = useCallback(() => {
    if (!bookingForAnchorRect) return { top: 0, left: 0 };

    const padding = 12;
    const viewportW = window.innerWidth || 0;
    const viewportH = window.innerHeight || 0;

    const modalW = 360;
    const modalH = 400;

    let left = bookingForAnchorRect.left;
    left = Math.max(padding, Math.min(left, viewportW - modalW - padding));

    // Open below the button (like a dropdown)
    let top = bookingForAnchorRect.bottom + 8;

    // If not enough space below, open above
    if (top + modalH + padding > viewportH) {
      top = bookingForAnchorRect.top - 8 - modalH;
    }

    top = Math.max(padding, Math.min(top, viewportH - modalH - padding));

    return { top, left };
  }, [bookingForAnchorRect]);

  const getPackageSelectorPosition = useCallback(() => {
    if (!packageSelectorAnchorRect) return { top: 0, left: 0, width: 280 };

    // Use absolute coordinates relative to the DOCUMENT, not the viewport
    const left = packageSelectorAnchorRect.left + window.scrollX;
    const top = packageSelectorAnchorRect.bottom + window.scrollY + 8;
    const width = packageSelectorAnchorRect.width;

    return { top, left, width };
  }, [packageSelectorAnchorRect]);

  const [currentTime, setCurrentTime] = useState(() => {
    const now = new Date();
    let hours = now.getHours();
    let minutes = now.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  });

  // Update pickup time every minute to show real-time
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      let hours = now.getHours();
      let minutes = now.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const newTime = `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
      setCurrentTime(newTime);
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleMouseDown = (e) => {
      const bookingForm = document.getElementById('booking-form');
      const target = e.target;

      // Skip if clicking inside time picker portal
      if (timePickerPortalRef.current && timePickerPortalRef.current.contains(target)) {
        return;
      }

      // Skip if clicking inside date picker portal
      if (datePickerPortalRef.current && datePickerPortalRef.current.contains(target)) {
        return;
      }
      if (target.closest('[data-datepicker-portal="true"]')) {
        return;
      }

      // Skip if clicking on any time picker anchor
      if (target.closest('[data-timepicker-anchor]')) {
        return;
      }

      // Skip if clicking on date picker anchor
      if (target.closest('[data-datepicker-anchor]')) {
        return;
      }

      // Skip if clicking inside package selector portal
      if (target.closest('[data-package-portal="true"]')) {
        return;
      }

      // Close From editing if clicking outside the from container
      if (isEditingFrom) {
        const fromContainer = document.querySelector('.from-location-input');
        if (fromContainer && !fromContainer.contains(target)) {
          setIsEditingFrom(false);
        }
      }

      // Close To editing if clicking outside the to container
      if (isEditingTo) {
        const toContainer = document.querySelector('.to-location-input');
        if (toContainer && !toContainer.contains(target)) {
          setIsEditingTo(false);
        }
      }

      // Close Airport editing if clicking outside
      if (isEditingAirport) {
        const airportWrapper = document.querySelector('.airport-editing-wrapper');
        if (
          airportWrapper &&
          !airportWrapper.contains(target) &&
          !target.closest('[data-airport-portal="true"]')
        ) {
          setIsEditingAirport(false);
        }
      }

      // Close City editing if clicking outside
      if (isEditingCity) {
        const cityWrapper = document.querySelector('.city-editing-wrapper');
        if (
          cityWrapper &&
          !cityWrapper.contains(target) &&
          !target.closest('[data-city-portal="true"]')
        ) {
          setIsEditingCity(false);
        }
      }

      // Close Package editing if clicking outside
      if (isEditingPackage) {
        const packageWrapper = document.querySelector('.package-editing-wrapper');
        const packagePortal = document.querySelector('[data-package-portal="true"]');
        if (packageWrapper && !packageWrapper.contains(target) && (!packagePortal || !packagePortal.contains(target))) {
          setIsEditingPackage(false);
          setOpenPackageSelector(false);
        }
      }

      // Close Time Picker if clicking outside
      if (openTimePicker) {
        if (!timePickerPortalRef.current?.contains(target) && !target.closest('[data-timepicker-anchor]')) {
          setOpenTimePicker(null);
          setTimePickerAnchorRect(null);
        }
      }

      // Close Date Picker if clicking outside
      if (openDatePicker) {
        if (
          !datePickerPortalRef.current?.contains(target) &&
          !target.closest('[data-datepicker-portal="true"]') &&
          !target.closest('[data-datepicker-anchor]')
        ) {
          setOpenDatePicker(null);
          setDatePickerAnchorRect(null);
        }
      }

      // Close date picker and other states if clicking outside booking form
      if (bookingForm && !bookingForm.contains(target)) {
        setIsEditingFrom(false);
        setIsEditingTo(false);
        setIsEditingAirport(false);
        setIsEditingCity(false);
        setOpenDatePicker(null);
        setDatePickerAnchorRect(null);
        setOpenPackageSelector(false);
      }
    };

    if (isEditingFrom || isEditingTo || openDatePicker || openPackageSelector || isEditingAirport || isEditingCity || isEditingPackage || openTimePicker) {
      document.addEventListener('mousedown', handleMouseDown);
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [isEditingFrom, isEditingTo, openDatePicker, openPackageSelector, isEditingAirport, isEditingCity, isEditingPackage, openTimePicker]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;

      // Check if clicking on booking for button
      if (target.closest('button') && target.closest('button').textContent.includes('Booking For')) {
        return;
      }

      // Check if clicking inside booking for modal dropdown
      const bookingForDropdowns = document.querySelectorAll('[data-booking-for-modal="true"]');
      let clickedInside = false;
      bookingForDropdowns.forEach(el => {
        if (el.contains(target)) clickedInside = true;
      });
      if (clickedInside) {
        return;
      }

      setIsBookingForModalOpen(false);
    };

    if (isBookingForModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isBookingForModalOpen]);

  // ── Close every panel — only one open at a time (MakeMyTrip style) ──
  const closeAllPanels = useCallback(() => {
    setOpenDatePicker(null);
    setDatePickerAnchorRect(null);
    setOpenTimePicker(null);
    setTimePickerAnchorRect(null);
    setIsBookingForModalOpen(false);
    setOpenPackageSelector(false);
    setPackageSearchTerm('');
    setIsEditingFrom(false);
    setIsEditingTo(false);
    setIsEditingAirport(false);
    setIsEditingCity(false);
    setIsEditingPackage(false);
  }, []);

  const openCalendar = useCallback((type, targetEl) => {
    // Close time picker and all other panels first
    setOpenTimePicker(null);
    setTimePickerAnchorRect(null);
    setOpenPackageSelector(false);
    setIsEditingFrom(false);
    setIsEditingTo(false);
    setIsEditingAirport(false);
    setIsEditingCity(false);

    if (!targetEl) {
      setOpenDatePicker(type);
      setDatePickerAnchorRect(null);
      return;
    }

    const rect = targetEl.getBoundingClientRect();
    setOpenDatePicker(type);
    setDatePickerAnchorRect({
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    });

    // Auto-scroll slightly if opening below overflows the viewport
    const pickerApproxH = 250;
    const overflow = (rect.bottom + 8 + pickerApproxH + 16) - (window.innerHeight || 0);
    if (overflow > 0) {
      window.scrollBy({ top: overflow, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!openDatePicker || !datePickerAnchorRect) return;

    const handleReposition = () => {
      const anchor = document.querySelector(`[data-datepicker-anchor="${openDatePicker}"]`);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setDatePickerAnchorRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    };

    const handleScroll = () => {
      const anchor = document.querySelector(`[data-datepicker-anchor="${openDatePicker}"]`);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setDatePickerAnchorRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDatePicker, datePickerAnchorRect]);

  // Time picker: stay open on scroll, reposition instead of closing
  useEffect(() => {
    if (!openTimePicker || !timePickerAnchorRect) return;

    const handleReposition = () => {
      const anchor = document.querySelector(`[data-timepicker-anchor="${openTimePicker}"]`);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setTimePickerAnchorRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    };

    const handleScroll = () => {
      const anchor = document.querySelector(`[data-timepicker-anchor="${openTimePicker}"]`);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setTimePickerAnchorRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
        width: rect.width,
        height: rect.height,
      });
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openTimePicker, timePickerAnchorRect]);

  // Reset hourly package when city changes to ensure consistency
  useEffect(() => {
    if (journeyType === "local" && localServiceType === "rental" && localCity) {
      // Check if currently selected package is valid for this city
      const selectedPkg = rentalPackages.find(pkg => pkg._id === hourlyPackage);
      const isValidForCity = selectedPkg && selectedPkg.serviceCities &&
        selectedPkg.serviceCities.some(sc =>
          sc.cityName && sc.cityName.toLowerCase().trim() === localCity.toLowerCase().trim()
        );

      // If not valid, reset to first available package for this city
      if (!isValidForCity) {
        const validPackage = rentalPackages.find(pkg =>
          pkg.serviceCities && Array.isArray(pkg.serviceCities) &&
          pkg.serviceCities.some(sc =>
            sc.cityName && sc.cityName.toLowerCase().trim() === localCity.toLowerCase().trim()
          )
        );
        setHourlyPackage(validPackage ? validPackage._id : "");
      }
    }
  }, [localCity, rentalPackages, hourlyPackage, journeyType, localServiceType, setHourlyPackage]);

  const getDatePickerPosition = useCallback(() => {
    if (!datePickerAnchorRect) return { top: 0, left: 0 };

    const padding = 16;
    const viewportW = window.innerWidth || 0;
    const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const pickerW = datePickerSize?.width || 580;

    // Horizontal positioning (document coordinates)
    let left = datePickerAnchorRect.left + scrollX;

    // If opening return date, align right edge of calendar with right edge of return box
    if (openDatePicker === 'return' || (datePickerAnchorRect.left + pickerW > viewportW - padding)) {
      left = datePickerAnchorRect.right - pickerW + scrollX;
    }

    // Keep within horizontal bounds
    const maxLeft = viewportW - pickerW - padding + scrollX;
    const minLeft = padding + scrollX;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // Open directly below input in document coordinates (MakeMyTrip style)
    const top = datePickerAnchorRect.bottom + scrollY + 8;

    return { top, left };
  }, [datePickerAnchorRect, datePickerSize, openDatePicker]);

  return (
    <section className={`hero-section relative min-h-screen flex items-center justify-center pt-0 ${corporateMember ? 'corporate-mode' : ''}`} style={{
      backgroundImage: `url("${heroBgImage}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: '#111827', // Dark slate background for better contrast while loading
      marginTop: '-64px'
    }}>
      <div className="hero-container max-w-[2000px] mx-auto px-4 sm:px-6 md:px-8 lg:px-6 w-full relative z-10">
        {/* Service Type Selection - Centered with higher z-index */}
        <div className="service-selector flex justify-center gap-6 sm:gap-8 md:gap-10 mb-0 relative z-20 py-1">
          {/* Out Station Service Button */}
          <button
            onClick={() => setJourneyType("outstation")}
            className={`service-btn flex-1 md:flex-initial group transition-all duration-300 hover:no-underline focus:outline-none focus:ring-0 focus:border-0 active:outline-none active:ring-0 active:border-0 border-0 outline-none flex flex-col items-center ${journeyType === "outstation" ? "service-btn-active" : ""}`}
          >
            <div
              className={`service-btn-content rounded-2xl p-2 sm:p-3 md:p-4 flex flex-col items-center justify-center transition-all duration-300 w-16 sm:w-20 md:w-[110px] h-16 sm:h-20 md:h-[110px] ${journeyType === "outstation"
                ? `shadow-lg md:shadow-xl ${corporateMember ? 'bg-[#F56753]' : 'bg-blue-600'}`
                : "bg-white border border-gray-200 md:shadow-md"
                }`}
              style={journeyType === "outstation" && window.innerWidth >= 768 ? { backgroundColor: corporateMember ? "#FEE2E2" : "#FEDEAD" } : {}}
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2Fad78fb2b059c49c6a4c498d58d4797a7%2Fc4c8e6abe88143008ae585517f00165d?format=webp&width=800&height=1200"
                alt="Out Station"
                className={`service-btn-icon w-8 sm:w-10 md:w-[50px] h-8 sm:h-10 md:h-[50px] transition-all duration-300 object-contain ${journeyType === "outstation" ? "brightness-0 invert md:brightness-100 md:invert-0" : ""}`}
              />
              <p
                className={`service-btn-label text-xs sm:text-xs md:text-sm font-bold mt-2 tracking-wide ${journeyType === "outstation" ? (corporateMember ? "text-[#F56753] md:text-gray-800" : "text-blue-600 md:text-gray-800") : "text-gray-600"}`}
              >
                Outstation
              </p>
            </div>
          </button>

          {/* Airport Service Button */}
          <button
            onClick={() => {
              setJourneyType("local");
              setLocalServiceType("airport");
            }}
            className={`service-btn flex-1 md:flex-initial group transition-all duration-300 hover:no-underline focus:outline-none focus:ring-0 focus:border-0 active:outline-none active:ring-0 active:border-0 border-0 outline-none flex flex-col items-center ${journeyType === "local" && localServiceType === "airport" ? "service-btn-active" : ""}`}
          >
            <div
              className={`service-btn-content rounded-2xl p-2 sm:p-3 md:p-4 flex flex-col items-center justify-center transition-all duration-300 w-16 sm:w-20 md:w-[110px] h-16 sm:h-20 md:h-[110px] ${journeyType === "local" && localServiceType === "airport"
                ? `shadow-lg md:shadow-xl ${corporateMember ? 'bg-[#F56753]' : 'bg-blue-600'}`
                : "bg-white border border-gray-200 md:shadow-md"
                }`}
              style={journeyType === "local" && localServiceType === "airport" && window.innerWidth >= 768 ? { backgroundColor: corporateMember ? "#FEE2E2" : "#FEDEAD" } : {}}
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2Fad78fb2b059c49c6a4c498d58d4797a7%2F2335eba30edd4cf593c2a7b0f92bfac3?format=webp&width=800&height=1200"
                alt="Airport"
                className={`service-btn-icon w-8 sm:w-10 md:w-[50px] h-8 sm:h-10 md:h-[50px] transition-all duration-300 object-contain ${journeyType === "local" && localServiceType === "airport" ? "brightness-0 invert md:brightness-100 md:invert-0" : ""}`}
              />
              <p
                className={`service-btn-label text-xs sm:text-xs md:text-sm font-bold mt-2 tracking-wide ${journeyType === "local" && localServiceType === "airport" ? (corporateMember ? "text-[#F56753] md:text-gray-800" : "text-blue-600 md:text-gray-800") : "text-gray-600"}`}
              >
                Airport
              </p>
            </div>
          </button>

          {/* Hourly Rental Service Button */}
          <button
            onClick={() => {
              setJourneyType("local");
              setLocalServiceType("rental");
            }}
            className={`service-btn flex-1 md:flex-initial group transition-all duration-300 hover:no-underline focus:outline-none focus:ring-0 focus:border-0 active:outline-none active:ring-0 active:border-0 border-0 outline-none flex flex-col items-center ${journeyType === "local" && localServiceType === "rental" ? "service-btn-active" : ""}`}
          >
            <div
              className={`service-btn-content rounded-2xl p-2 sm:p-3 md:p-4 flex flex-col items-center justify-center transition-all duration-300 w-16 sm:w-20 md:w-[110px] h-16 sm:h-20 md:h-[110px] ${journeyType === "local" && localServiceType === "rental"
                ? `shadow-lg md:shadow-xl ${corporateMember ? 'bg-[#F56753]' : 'bg-blue-600'}`
                : "bg-white border border-gray-200 md:shadow-md"
                }`}
              style={journeyType === "local" && localServiceType === "rental" && window.innerWidth >= 768 ? { backgroundColor: corporateMember ? "#FEE2E2" : "#FEDEAD" } : {}}
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2Fad78fb2b059c49c6a4c498d58d4797a7%2F9cd222b251fa4bbdb73134f4ab7a923b?format=webp&width=800&height=1200"
                alt="Hourly Rental"
                className={`service-btn-icon w-8 sm:w-10 md:w-[50px] h-8 sm:h-10 md:h-[50px] transition-all duration-300 object-contain ${journeyType === "local" && localServiceType === "rental" ? "brightness-0 invert md:brightness-100 md:invert-0" : ""}`}
              />
              <p
                className={`service-btn-label text-xs sm:text-xs md:text-sm font-bold mt-2 tracking-wide ${journeyType === "local" && localServiceType === "rental" ? (corporateMember ? "text-[#F56753] md:text-gray-800" : "text-blue-600 md:text-gray-800") : "text-gray-600"}`}
              >
                Hourly Rental
              </p>
            </div>
          </button>
        </div>



        {/* Booking Form Container - Centered dynamically with icons */}
        <div
          id="booking-form"
          className={`booking-form-card bg-white rounded-xl shadow-2xl w-full max-w-full xl:max-w-[1180px] mx-auto border border-gray-300 md:relative ${journeyType === "outstation" || (journeyType === "local" && localServiceType === "airport")
            ? "md:-mt-[75px] pt-4 md:pt-[72px]"
            : "md:-mt-[75px] pt-4 md:pt-[72px]"
            }`}
          style={{
            boxShadow: "0 25px 70px rgba(0, 0, 0, 0.15)",
            zIndex: 5
          }}
        >
          {/* Trip Type Selection - INSIDE the white card on desktop */}
          <div className={`hidden md:flex justify-center gap-4 sm:gap-6 mb-0 relative z-20 pt-4 ${journeyType === "local" && localServiceType === "rental" ? "invisible h-0 !pt-0 !mb-0" : ""}`}>
            <button
              type="button"
              onClick={() => {
                if (journeyType === "local" && localServiceType === "airport") {
                  setTransferDirection("to");
                } else {
                  setJourneyType("outstation");
                  setTripType("oneWay");
                }
              }}
              className={`trip-option flex items-center gap-2 px-6 py-2 rounded-full border ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "to" : journeyType === "outstation" && tripType === "oneWay"))
                ? (corporateMember ? "bg-red-50 border-[#F56753]/30" : "bg-blue-100 border-blue-400")
                : "bg-white border-gray-300"
                }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "to" : journeyType === "outstation" && tripType === "oneWay")) ? (corporateMember ? "border-[#F56753]" : "border-blue-600") : "border-gray-400"}`}>
                {((journeyType === "local" && localServiceType === "airport" ? transferDirection === "to" : journeyType === "outstation" && tripType === "oneWay")) && <div className={`w-2 h-2 rounded-full ${corporateMember ? "bg-[#F56753]" : "bg-blue-600"}`}></div>}
              </div>
              <span className={`text-sm font-bold transition-colors duration-200 ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "to" : journeyType === "outstation" && tripType === "oneWay")) ? (corporateMember ? "text-[#F56753]" : "text-blue-700") : "text-gray-800"}`}>
                {journeyType === "local" && localServiceType === "airport" ? "To Airport" : "One Way"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (journeyType === "local" && localServiceType === "airport") {
                  setTransferDirection("from");
                } else {
                  setJourneyType("outstation");
                  setTripType("roundTrip");
                }
              }}
              className={`trip-option flex items-center gap-2 px-6 py-2 rounded-full border ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "from" : journeyType === "outstation" && tripType === "roundTrip"))
                ? (corporateMember ? "bg-red-50 border-[#F56753]/30" : "bg-blue-100 border-blue-400")
                : "bg-white border-gray-300"
                }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "from" : journeyType === "outstation" && tripType === "roundTrip")) ? (corporateMember ? "border-[#F56753]" : "border-blue-600") : "border-gray-400"}`}>
                {((journeyType === "local" && localServiceType === "airport" ? transferDirection === "from" : journeyType === "outstation" && tripType === "roundTrip")) && <div className={`w-2 h-2 rounded-full ${corporateMember ? "bg-[#F56753]" : "bg-blue-600"}`}></div>}
              </div>
              <span className={`text-sm font-bold transition-colors duration-200 ${((journeyType === "local" && localServiceType === "airport" ? transferDirection === "from" : journeyType === "outstation" && tripType === "roundTrip")) ? (corporateMember ? "text-[#F56753]" : "text-blue-700") : "text-gray-800"}`}>
                {journeyType === "local" && localServiceType === "airport" ? "From Airport" : "Round Trip"}
              </span>
            </button>
          </div>

          <div className="px-[8px] pb-4 pt-2 sm:px-6 sm:pb-6 sm:pt-2 md:px-8 md:pb-8 md:pt-4" style={{ overflow: 'visible' }}>
            <form onSubmit={(e) => {
              // Check if user is logged in - either regular user or corporate user
              const corporateToken = localStorage.getItem("corporateToken");
              const isLoggedIn = !!user || !!corporateToken;

              if (!isLoggedIn) {
                e.preventDefault();
                localStorage.setItem("pendingSubmitAfterLogin", "true");
                setIsLoginModalOpen(true);
                return;
              }
              handleSubmit(e);
            }} className="text-gray-800" style={{ overflow: 'visible' }}>
              {/* MOBILE VIEW (High-Fidelity MMT style) */}
              <div className="block md:hidden space-y-0 pb-4">
                {journeyType === "outstation" && (
                  <>
                    <div className="trip-selector-container-mobile">
                      <div className="mobile-trip-type-icon">
                        <TripModeIcon />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setJourneyType("outstation");
                          setTripType("oneWay");
                        }}
                        className={`trip-option ${tripType === "oneWay" ? "active-mobile" : ""}`}
                      >
                        <div className="mobile-radio-circle"></div>
                        One Way
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setJourneyType("outstation");
                          setTripType("roundTrip");
                        }}
                        className={`trip-option ${tripType === "roundTrip" ? "active-mobile" : ""}`}
                      >
                        <div className="mobile-radio-circle"></div>
                        Round Trip
                      </button>
                    </div>
                    <div className="relative">
                      <div className="relative">
                        <div className="mobile-field-card flex items-start gap-4" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); setMobileSearchModal({ isOpen: true, type: 'from' }); }}>
                          <div className="self-center flex-shrink-0 pl-1">
                            <FromIcon />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="mobile-field-label">FROM LOCATION</div>
                            <div className="mobile-field-value truncate">
                              {pickup ? stripPlusCode(pickup).split(',')[0] : 'Select City'}
                              <span className="mobile-field-subvalue text-gray-500 truncate">
                                {pickup ? (stripPlusCode(pickup).split(',').slice(1).join(', ') || '') : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Floating Swap Button */}
                        {additionalCities.length === 0 && (
                          <div className="absolute right-6 bottom-0 translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center text-blue-500 pointer-events-auto cursor-pointer translate-x-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              const t = pickup;
                              setPickup(dropoff);
                              setDropoff(t);
                              const tp = pickupPlace;
                              setPickupPlace(dropoffPlace);
                              setDropoffPlace(tp);
                            }}>
                            <FaExchangeAlt className="rotate-90 text-sm" />
                          </div>
                        )}
                      </div>

                      {/* Additional stops in between From and To */}
                      {additionalCities.map((city, index) => (
                        <div key={city.id} className="relative mb-2 px-0">
                          <div 
                            className="mobile-field-card flex items-start gap-4" 
                            style={{ marginBottom: '8px' }}
                            onClick={() => handleMobileSearchOpen(`stop-${index}`)}
                          >
                            <div className="self-center flex-shrink-0 pl-1">
                              {/* Gray stop indicator circle */}
                              <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-400 flex items-center justify-center bg-gray-50">
                                <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="mobile-field-label">STOP {index + 1}</div>
                              <div className="mobile-field-value truncate">
                                {city.value ? city.value.split(',')[0] : 'Search stop location'}
                                <span className="mobile-field-subvalue text-gray-500 truncate">
                                  {city.value ? (city.value.split(',').slice(1).join(',') || '') : 'Enter stop details'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const updatedCities = additionalCities.filter(s => s.id !== city.id);
                              setAdditionalCities(updatedCities);
                              if (updatedCities.length === 0) {
                                setMultiCity(false);
                              }
                            }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 text-red-500 flex items-center justify-center text-lg active:scale-90 z-10"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}

                      <div className="mobile-field-card flex items-start gap-4" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); setMobileSearchModal({ isOpen: true, type: 'to' }); }}>
                        <div className="self-center flex-shrink-0 pl-1">
                          <ToIcon />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mobile-field-label">TO</div>
                          <div className="mobile-field-value truncate">
                            {dropoff ? stripPlusCode(dropoff).split(',')[0] : 'Select City'}
                            <span className="mobile-field-subvalue text-gray-500 truncate">
                              {dropoff ? (stripPlusCode(dropoff).split(',').slice(1).join(', ') || '') : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button 
                      type="button" 
                      className="mobile-add-stops-btn transition-all active:scale-95" 
                      style={{ marginBottom: '8px', marginLeft: '10px' }} 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setAdditionalCities([...additionalCities, { id: Date.now(), value: "" }]);
                        setMultiCity(true);
                      }}
                    >
                      + ADD STOPS
                    </button>

                    {tripType === 'oneWay' && (
                      <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={(e) => { e.stopPropagation(); setMobileDateTimeSheet({ isOpen: true, type: 'start' }); }}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <CalendarIcon />
                          <div className="mobile-field-label uppercase text-[10px] !mb-0">TRIP START</div>
                        </div>
                        <div className="mobile-field-value !text-[15px] truncate flex items-center gap-1.5 line-clamp-1">
                          {pickupDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          <span className="font-bold text-blue-500 text-[13px]">
                            {pickupTime || (pickupDate.toDateString() === new Date().toDateString() ? currentTime : "10:00 AM")}
                          </span>
                        </div>
                        <div className="mobile-field-subvalue !ml-0 text-[10px] font-bold text-gray-500 uppercase">
                          {pickupDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                        </div>
                      </div>
                    )}

                    {tripType === 'roundTrip' && (
                      <div className="mx-[10px] mb-4 flex gap-2" style={{ overflow: 'visible' }}>
                        {/* Trip Start Card */}
                        <div 
                          className="flex-1 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer flex flex-col justify-between"
                          onClick={(e) => { e.stopPropagation(); setMobileDateTimeSheet({ isOpen: true, type: 'start' }); }}
                        >
                          <div>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide leading-none mb-1">TRIP START</div>
                            <div className="text-[13px] font-bold text-gray-900 leading-tight">
                              {pickupDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                            </div>
                            <div className="text-[12px] text-gray-500 mt-0.5">
                              {pickupTime || (pickupDate.toDateString() === new Date().toDateString() ? currentTime : "10:00 AM")}
                            </div>
                          </div>
                        </div>

                        {/* Trip End Card with Close (x) Button */}
                        <div 
                          className="flex-1 bg-white p-3 rounded-lg border-2 border-blue-500 cursor-pointer relative flex flex-col justify-between"
                          onClick={(e) => { e.stopPropagation(); setMobileDateTimeSheet({ isOpen: true, type: 'end' }); }}
                        >
                          {/* Close Button */}
                          <button
                            type="button"
                            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-30"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTripType('oneWay');
                            }}
                          >
                            <FaTimes className="text-gray-500 text-[10px]" />
                          </button>

                          <div>
                            <div className="text-[10px] font-bold text-blue-500 uppercase tracking-wide leading-none mb-1">TRIP END</div>
                            <div className="text-[13px] font-bold text-gray-900 leading-tight">
                              {returnDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {returnDate.getFullYear()}
                            </div>
                            <div className="text-[11px] font-bold text-gray-500 mt-0.5">
                              {returnDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {corporateMember && (
                      <div 
                        className="mobile-field-card cursor-pointer" 
                        style={{ marginBottom: '8px' }} 
                        onClick={(e) => { 
                          const rect = e.currentTarget.getBoundingClientRect();
                          closeAllPanels();
                          setBookingForAnchorRect({
                            top: rect.top,
                            bottom: rect.bottom,
                            left: rect.left,
                            right: rect.right,
                          });
                          setIsBookingForModalOpen(!isBookingForModalOpen);
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FaUser className="text-[#F56753] text-xs" />
                          <div className="mobile-field-label uppercase text-[10px] !mb-0">BOOKING FOR</div>
                        </div>
                        <div className="mobile-field-value !text-[15px] truncate flex items-center justify-between gap-1.5 line-clamp-1 w-full">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {selectedBookingFor?.profileImageUrl && (
                              <img
                                src={selectedBookingFor.profileImageUrl}
                                alt=""
                                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                              />
                            )}
                            <span className="font-bold text-[#F56753] truncate">
                              {selectedBookingFor
                                ? (selectedBookingFor.isGuest ? "Guest" : (selectedBookingFor.fullName || selectedBookingFor.name))
                                : "Myself"}
                            </span>
                          </div>
                          <svg 
                            className={`w-4 h-4 text-[#F56753] transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {journeyType === "local" && localServiceType === "rental" && (
                  <>
                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); setMobileSearchModal({ isOpen: true, type: 'city' }); }}>
                      <div className="mobile-field-label">CITY</div>
                      <div className="mobile-field-value truncate">
                        {localCity || 'Select City'}
                      </div>
                    </div>

                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); handleMobileSearchOpen('package'); }}>
                      <div className="mobile-field-label">SELECT PACKAGE</div>
                      <div className="mobile-field-value truncate">
                        {hourlyPackage ? rentalPackages.find(p => p._id === hourlyPackage)?.name || 'Choose Package' : 'Choose Package'}
                      </div>
                    </div>

                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={(e) => { e.stopPropagation(); setMobileDateTimeSheet({ isOpen: true, type: 'start' }); }}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <CalendarIcon />
                        <div className="mobile-field-label uppercase text-[10px] !mb-0">PICK UP TIME</div>
                      </div>
                      <div className="mobile-field-value !text-[15px] truncate flex items-center gap-1.5 line-clamp-1">
                        {pickupDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        <span className="font-bold text-blue-500 text-[13px]">
                          {pickupTime || (pickupDate.toDateString() === new Date().toDateString() ? currentTime : "10:00 AM")}
                        </span>
                      </div>
                      <div className="mobile-field-subvalue !ml-0 text-[10px] font-bold text-gray-500 uppercase">
                        {pickupDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                      </div>
                    </div>
                    {corporateMember && (
                      <div 
                        className="mobile-field-card cursor-pointer" 
                        style={{ marginBottom: '8px' }} 
                        onClick={(e) => { 
                          const rect = e.currentTarget.getBoundingClientRect();
                          closeAllPanels();
                          setBookingForAnchorRect({
                            top: rect.top,
                            bottom: rect.bottom,
                            left: rect.left,
                            right: rect.right,
                          });
                          setIsBookingForModalOpen(!isBookingForModalOpen);
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FaUser className="text-[#F56753] text-xs" />
                          <div className="mobile-field-label uppercase text-[10px] !mb-0">BOOKING FOR</div>
                        </div>
                        <div className="mobile-field-value !text-[15px] truncate flex items-center justify-between gap-1.5 line-clamp-1 w-full">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {selectedBookingFor?.profileImageUrl && (
                              <img
                                src={selectedBookingFor.profileImageUrl}
                                alt=""
                                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                              />
                            )}
                            <span className="font-bold text-[#F56753] truncate">
                              {selectedBookingFor
                                ? (selectedBookingFor.isGuest ? "Guest" : (selectedBookingFor.fullName || selectedBookingFor.name))
                                : "Myself"}
                            </span>
                          </div>
                          <svg 
                            className={`w-4 h-4 text-[#F56753] transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {journeyType === "local" && localServiceType === "airport" && (
                  <>
                    <div className="trip-selector-container-mobile">
                      <div className="mobile-trip-type-icon">
                        <TripModeIcon />
                      </div>
                      <button
                        type="button"
                        onClick={() => setTransferDirection("to")}
                        className={`trip-option ${transferDirection === "to" ? "active-mobile" : ""}`}
                      >
                        <div className="mobile-radio-circle"></div>
                        To Airport
                      </button>
                      <button
                        type="button"
                        onClick={() => setTransferDirection("from")}
                        className={`trip-option ${transferDirection === "from" ? "active-mobile" : ""}`}
                      >
                        <div className="mobile-radio-circle"></div>
                        From Airport
                      </button>
                    </div>

                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); handleMobileSearchOpen('airport'); }}>
                      <div className="mobile-field-label">AIRPORT</div>
                      <div className="mobile-field-value truncate">
                        {selectedAirport
                          ? (() => { const ap = airports.find(a => a.code === selectedAirport); return ap ? `${ap.code} — ${ap.name}` : selectedAirport; })()
                          : 'Select Airport'}
                      </div>
                    </div>

                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={() => { closeAllPanels(); handleMobileSearchOpen('airport-city'); }}>
                      <div className="mobile-field-label">{transferDirection === 'to' ? 'PICKUP CITY' : 'DROP CITY'}</div>
                      <div className="mobile-field-value truncate">
                        {dropoff ? stripPlusCode(dropoff).split(',')[0] : (transferDirection === 'to' ? 'Select Pickup City' : 'Select Drop City')}
                      </div>
                    </div>

                    <div className="mobile-field-card" style={{ marginBottom: '8px' }} onClick={(e) => { e.stopPropagation(); setMobileDateTimeSheet({ isOpen: true, type: 'start' }); }}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <CalendarIcon />
                        <div className="mobile-field-label uppercase text-[10px] !mb-0">PICK UP TIME</div>
                      </div>
                      <div className="mobile-field-value !text-[15px] truncate flex items-center gap-1.5 line-clamp-1">
                        {pickupDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        <span className="font-bold text-blue-500 text-[13px]">
                          {pickupTime || (pickupDate.toDateString() === new Date().toDateString() ? currentTime : "10:00 AM")}
                        </span>
                      </div>
                      <div className="mobile-field-subvalue !ml-0 text-[10px] font-bold text-gray-500 uppercase">
                        {pickupDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                      </div>
                    </div>
                    {corporateMember && (
                      <div 
                        className="mobile-field-card cursor-pointer" 
                        style={{ marginBottom: '8px' }} 
                        onClick={(e) => { 
                          const rect = e.currentTarget.getBoundingClientRect();
                          closeAllPanels();
                          setBookingForAnchorRect({
                            top: rect.top,
                            bottom: rect.bottom,
                            left: rect.left,
                            right: rect.right,
                          });
                          setIsBookingForModalOpen(!isBookingForModalOpen);
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FaUser className="text-[#F56753] text-xs" />
                          <div className="mobile-field-label uppercase text-[10px] !mb-0">BOOKING FOR</div>
                        </div>
                        <div className="mobile-field-value !text-[15px] truncate flex items-center justify-between gap-1.5 line-clamp-1 w-full">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {selectedBookingFor?.profileImageUrl && (
                              <img
                                src={selectedBookingFor.profileImageUrl}
                                alt=""
                                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                              />
                            )}
                            <span className="font-bold text-[#F56753] truncate">
                              {selectedBookingFor
                                ? (selectedBookingFor.isGuest ? "Guest" : (selectedBookingFor.fullName || selectedBookingFor.name))
                                : "Myself"}
                            </span>
                          </div>
                          <svg 
                            className={`w-4 h-4 text-[#F56753] transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="px-4 mt-6 pb-1">
                  <button type="submit" className="mobile-search-btn">
                    {isLoading ? 'Searching...' : 'SEARCH CABS'}
                  </button>
                </div>
              </div>

              {/* DESKTOP VIEW (Visible on Tablet/Desktop) */}
              <div className="hidden md:block space-y-3">
                {/* Location and Date/Time Grid */}
                <div className="booking-fields-grid w-full mx-auto " style={{ overflow: 'visible' }}>
                  {journeyType === "outstation" ? (
                    <>
                      {/* Form Fields Container with Border */}
                      <div
                        className="border border-gray-200 w-[calc(100%+24px)] -mx-3 rounded-xl bg-white"
                        style={{
                          overflow: 'visible',
                          position: 'relative'
                        }}
                      >
                        {/* Single Row Layout - Full Width */}
                        <div
                          className="flex items-stretch w-full h-[110px]"
                          style={{ overflow: 'visible', position: 'relative' }}
                        >
                          {/* FROM Location */}
                          {!isEditingFrom ? (
                            <div
                              className="location-box-hover flex-1 min-w-[180px] pl-4 pr-10 py-3 cursor-pointer flex flex-col justify-center rounded-l-xl relative"
                              style={{ zIndex: 10 }}
                              onClick={() => {
                                closeAllPanels();
                                setIsEditingFrom(true);
                                setTimeout(() => {
                                  const inp = document.querySelector('.from-location-input .location-input-field');
                                  if (inp) inp.focus();
                                }, 50);
                              }}
                            >
                              <div className="font-bold text-gray-400 mb-1 text-xs sm:text-sm tracking-wider uppercase">FROM</div>
                              <div className="font-bold text-gray-900 truncate text-xl sm:text-2xl md:text-[28px] leading-tight pb-0.5" title={pickup}>
                                {pickup ? stripPlusCode(pickup).split(',')[0] : 'Select City'}
                              </div>
                              {pickup && stripPlusCode(pickup).split(',').length > 1 && (
                                <div className="text-xs text-gray-500 truncate max-w-full">
                                  {stripPlusCode(pickup).split(',').slice(1).join(', ').trim()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className="from-location-input flex-1 min-w-[180px] rounded-l-xl relative"
                              style={{ zIndex: 50, overflow: 'visible' }}
                            >
                              <LocationInput
                                label="From"
                                value={pickup}
                                onChange={(value) => {
                                  setPickup(value);
                                  if (!value) setPickupPlace(null);
                                }}
                                onPlaceSelected={(place) => {
                                  if (place) {
                                    const normalizedPlace = {
                                      ...place,
                                      location:
                                        place.geometry && place.geometry.location
                                          ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
                                          : place.location || null,
                                    };
                                    setPickupPlace(normalizedPlace);
                                    setPickup(place.formatted_address || place.name || "");
                                    setIsEditingFrom(false);
                                  }
                                }}
                                placeholder="From"
                                required
                              />
                            </div>
                          )}

                          {/* Divider with Swap Button */}
                          <div className="relative self-stretch w-0 flex-shrink-0 z-20">
                            {/* Vertical Line - Full height centered behind swap icon */}
                            <div className="w-px h-full bg-gray-300 absolute left-1/2 -translate-x-1/2"></div>
                            {/* Swap Button on top centered */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              <button
                                type="button"
                                onClick={() => {
                                  const temp = pickup;
                                  setPickup(dropoff);
                                  setDropoff(temp);
                                  const tempPlace = pickupPlace;
                                  setPickupPlace(dropoffPlace);
                                  setDropoffPlace(tempPlace);
                                  setIsEditingFrom(false);
                                  setIsEditingTo(false);
                                }}
                                className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center hover:bg-orange-200 transition-colors flex-shrink-0 relative z-10"
                                title="Swap locations"
                              >
                                <img src="https://cdn.builder.io/api/v1/image/assets%2Faa96de3d986f4f74ad1dc4ba97353d1c%2F5ad47c5b66fa44fb9f5f3d4901b88517?format=webp&width=800&height=1200" alt="swap" className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* TO Location */}
                          {!isEditingTo ? (
                            <div
                              className="location-box-hover flex-1 min-w-[180px] pl-10 pr-4 py-3 cursor-pointer flex flex-col justify-center relative"
                              style={{ zIndex: 10 }}
                              onClick={() => {
                                closeAllPanels();
                                setIsEditingTo(true);
                                setTimeout(() => {
                                  const inp = document.querySelector('.to-location-input .location-input-field');
                                  if (inp) inp.focus();
                                }, 50);
                              }}
                            >
                              <div className="font-bold text-gray-400 mb-1 text-xs sm:text-sm tracking-wider uppercase">TO</div>
                              <div className="font-bold text-gray-900 truncate text-xl sm:text-2xl md:text-[28px] leading-tight pb-0.5" title={dropoff}>
                                {dropoff ? stripPlusCode(dropoff).split(',')[0] : 'Select City'}
                              </div>
                              {dropoff && stripPlusCode(dropoff).split(',').length > 1 && (
                                <div className="text-xs text-gray-500 truncate max-w-full">
                                  {stripPlusCode(dropoff).split(',').slice(1).join(', ').trim()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className="to-location-input flex-1 min-w-[180px] relative"
                              style={{ zIndex: 50, overflow: 'visible' }}
                            >
                              <LocationInput
                                label="To"
                                value={dropoff}
                                onChange={(value) => {
                                  setDropoff(value);
                                  if (!value) setDropoffPlace(null);
                                }}
                                onPlaceSelected={(place) => {
                                  if (place) {
                                    const normalizedPlace = {
                                      ...place,
                                      location:
                                        place.geometry && place.geometry.location
                                          ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
                                          : place.location || null,
                                    };
                                    setDropoffPlace(normalizedPlace);
                                    setDropoff(place.formatted_address || place.name || "");
                                    setIsEditingTo(false);
                                  }
                                }}
                                placeholder="To"
                                required
                              />
                            </div>
                          )}

                          {/* Vertical Divider - Between To and Departure */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Departure Date - Fixed width */}
                          <div
                            className={`field-hover flex-shrink-0 cursor-pointer w-36 px-2 pt-3 pb-2 relative flex flex-col justify-start ${openDatePicker === 'departure' ? 'field-hover--active' : ''}`}
                            data-datepicker-anchor="departure"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openDatePicker === 'departure') {
                                setOpenDatePicker(null);
                                setDatePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              openCalendar('departure', e.currentTarget);
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">DEPARTURE</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openDatePicker === "departure" ? "rotate-180" : ""}`}
                              />
                            </div>
                            <div className="relative">
                              <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate">
                                {pickupDate ? pickupDate.getDate() : '19'}<span className="text-base sm:text-lg md:text-xl">{pickupDate ? pickupDate.toLocaleString('default', { month: 'short' }) : "Feb"}&apos;{pickupDate ? pickupDate.getFullYear().toString().slice(-2) : '26'}</span>
                              </div>
                              <div className="text-gray-500 text-sm sm:text-base truncate">
                                {pickupDate ? pickupDate.toLocaleString('default', { weekday: 'long' }) : 'Thursday'}
                              </div>
                            </div>
                          </div>

                          {/* Vertical Divider - Between Departure and Return */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Return Date - Fixed width */}
                          <div
                            className={`field-hover flex-shrink-0 cursor-pointer px-2 pt-3 pb-2 relative flex flex-col justify-start ${tripType === 'roundTrip' ? 'w-36' : 'w-48'} ${openDatePicker === 'return' ? 'field-hover--active' : ''}`}
                            data-datepicker-anchor="return"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (tripType !== 'roundTrip') {
                                setTripType('roundTrip');
                              }
                              if (openDatePicker === 'return') {
                                setOpenDatePicker(null);
                                setDatePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              openCalendar('return', e.currentTarget);
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">RETURN</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openDatePicker === "return" ? "rotate-180" : ""
                                  }`}
                              />



                            </div>
                            {tripType === 'roundTrip' && returnDate ? (
                              <>
                                <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate">
                                  {returnDate.getDate()}<span className="text-base sm:text-lg md:text-xl">{returnDate.toLocaleString('default', { month: 'short' })}&apos;{returnDate.getFullYear().toString().slice(-2)}</span>
                                </div>
                                <div className="text-gray-500 text-sm sm:text-base truncate">
                                  {returnDate.toLocaleString('default', { weekday: 'long' })}
                                </div>
                              </>
                            ) : (
                              <div className="relative">
                                <div className="font-bold text-gray-900 invisible text-xl sm:text-2xl md:text-3xl">
                                  00<span className="text-base sm:text-lg md:text-xl">Jan&apos;00</span>
                                </div>
                                <div className="text-black text-[13.6px] font-bold leading-[100%] tracking-[0.01em] absolute top-0 left-0 truncate">
                                  Tap to add return date
                                  <br />
                                  for bigger discounts
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Vertical Divider - Between Return and Pickup Time */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Pickup Time - Fixed width */}
                          <div
                            ref={timePickerAnchorRef}
                            className={`field-hover flex-shrink-0 w-32 px-2 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start ${(!corporateMember && tripType !== 'roundTrip') ? 'rounded-r-xl' : ''} ${openTimePicker === 'pickup' ? 'field-hover--active' : ''}`}
                            data-timepicker-anchor="pickup"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openTimePicker === 'pickup') {
                                setOpenTimePicker(null);
                                setTimePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              setSelectedTime(getValidInitialTime(pickupTime, pickupDate));
                              const rect = e.currentTarget.getBoundingClientRect();
                              setOpenTimePicker('pickup');
                              setTimePickerAnchorRect({
                                top: rect.top,
                                bottom: rect.bottom,
                                left: rect.left,
                                right: rect.right,
                                width: rect.width,
                                height: rect.height,
                              });
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">PICKUP TIME</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openTimePicker === "pickup" ? "rotate-180" : ""}`}
                              />
                            </div>
                            <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl whitespace-nowrap flex items-baseline gap-1">
                              <span>{(pickupTime || "10:00 AM").split(" ")[0]}</span>
                              <span className="text-sm font-semibold">
                                {(pickupTime || "10:00 AM").split(" ")[1]}
                              </span>
                            </div>
                          </div>

                          {/* Drop Time - Only for Round Trip */}
                          {tripType === 'roundTrip' && (
                            <>
                              <div className="w-px self-stretch bg-gray-300"></div>
                              <div
                                className={`field-hover flex-shrink-0 w-32 px-2 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start ${!corporateMember ? 'rounded-r-xl' : ''} ${openTimePicker === 'drop' ? 'field-hover--active' : ''}`}
                                data-timepicker-anchor="drop"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openTimePicker === 'drop') {
                                    setOpenTimePicker(null);
                                    setTimePickerAnchorRect(null);
                                    return;
                                  }
                                  closeAllPanels();
                                  setSelectedTime(getValidInitialTime(returnTime, returnDate));
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setOpenTimePicker('drop');
                                  setTimePickerAnchorRect({
                                    top: rect.top,
                                    bottom: rect.bottom,
                                    left: rect.left,
                                    right: rect.right,
                                    width: rect.width,
                                    height: rect.height,
                                  });
                                }}
                              >
                                <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                                  <span className="truncate">DROP TIME</span>
                                  <img
                                    src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                    alt="dropdown"
                                    className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openTimePicker === "drop" ? "rotate-180" : ""
                                      }`}
                                  />
                                </div>
                                <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl whitespace-nowrap flex items-baseline gap-1">
                                  <span>{(returnTime || "10:00 AM").split(" ")[0]}</span>
                                  <span className="text-sm font-semibold">
                                    {(returnTime || "10:00 AM").split(" ")[1]}
                                  </span>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Booking For - Corporate Users Only */}
                          {corporateMember && (
                            <>
                              <div className="w-px self-stretch bg-gray-300"></div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  closeAllPanels();
                                  setBookingForAnchorRect({
                                    top: rect.top,
                                    bottom: rect.bottom,
                                    left: rect.left,
                                    right: rect.right,
                                  });
                                  setIsBookingForModalOpen(!isBookingForModalOpen);
                                }}
                                className={`field-hover flex-shrink-0 w-44 px-3 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start rounded-r-xl ${isBookingForModalOpen ? 'field-hover--active' : ''}`}
                              >
                                <div className="text-gray-400 mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate text-left">
                                  <span className="truncate">BOOKING FOR</span>
                                  <img
                                    src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                    alt="dropdown"
                                    className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`}
                                  />
                                </div>
                                <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? "Guest"
                                    : (selectedBookingFor?.fullName || selectedBookingFor?.name || corporateMember?.fullName || corporateMember?.name || "Corporate User")}
                                </div>
                                <div className="text-gray-500 text-sm truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? ""
                                    : (selectedBookingFor?.email || corporateMember?.email || "")}
                                </div>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  ) : localServiceType === "rental" ? (
                    <>
                      {/* Hourly Rental - Same Design as Outstation */}
                      <div className="border border-gray-200 rounded-xl bg-white mb-7" style={{ overflow: 'visible' }}>
                        <div className="flex items-stretch w-full h-[110px]" style={{ overflow: 'visible' }}>
                          {/* City Selection - display/edit mode like FROM/TO */}
                          {!isEditingCity ? (
                            <div
                              className="location-box-hover cursor-pointer flex-1 min-w-0 px-4 py-3 flex flex-col justify-center rounded-l-xl relative"
                              style={{ zIndex: 10 }}
                              onClick={() => {
                                closeAllPanels();
                                setIsEditingCity(true);
                              }}
                            >
                              <div className="font-bold text-gray-400 mb-1 text-xs sm:text-sm tracking-wider uppercase">CITY</div>
                              <div className="font-bold text-gray-900 truncate text-2xl sm:text-3xl md:text-[34px] leading-normal pb-1">
                                {localCity || 'Select City'}
                              </div>
                            </div>
                          ) : (
                            <div
                              className="city-editing-wrapper flex-1 min-w-0 rounded-l-xl relative"
                              style={{ zIndex: 50, overflow: 'visible' }}
                            >
                              <CitySelector
                                cities={[...new Set(rentalPackages.filter((pkg) => pkg.serviceCities && Array.isArray(pkg.serviceCities) && pkg.serviceCities.length > 0).flatMap((pkg) => pkg.serviceCities.map((sc) => (sc.cityName || sc.name || sc)).filter(Boolean)))].sort()}
                                selectedCity={localCity}
                                onCitySelected={(city) => {
                                  setLocalCity(city);
                                  if (!city) setHourlyPackage("");
                                  setIsEditingCity(false);
                                }}
                                placeholder="Select City"
                                autoOpen={true}
                                onClose={() => setIsEditingCity(false)}
                              />
                            </div>
                          )}

                          {/* Vertical Divider */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Package - Flex grow */}
                          {!isEditingPackage ? (
                            <div
                              className="location-box-hover cursor-pointer flex-1 min-w-0 px-4 py-3 relative flex flex-col justify-center"
                              style={{ zIndex: 10 }}
                              onClick={() => {
                                closeAllPanels();
                                setIsEditingPackage(true);
                                setOpenPackageSelector(true);
                                setTimeout(() => packageSearchInputRef.current?.focus(), 50);
                              }}
                            >
                              <div className="font-bold text-gray-400 mb-1 text-xs sm:text-sm tracking-wider uppercase">PACKAGE</div>
                              <div className="font-bold text-gray-900 truncate text-2xl sm:text-3xl md:text-[34px] leading-normal pb-1">
                                {hourlyPackage ? rentalPackages.find(p => p._id === hourlyPackage)?.name || 'Choose Package' : 'Choose Package'}
                              </div>
                              {hourlyPackage && (
                                <div className="text-gray-500 text-sm sm:text-base truncate">
                                  {(() => {
                                    const pkg = rentalPackages.find(p => p._id === hourlyPackage);
                                    if (!pkg) return '';
                                    const duration = pkg.duration || pkg.hr || '';
                                    const distance = pkg.distance || pkg.km || '';
                                    return `${duration} hrs / ${distance} kms`;
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className="package-editing-wrapper flex-1 min-w-0 relative flex flex-col"
                              style={{ zIndex: 50, overflow: 'visible' }}
                              ref={(el) => {
                                if (el) {
                                  const rect = el.getBoundingClientRect();
                                  if (!packageSelectorAnchorRect || packageSelectorAnchorRect.top !== rect.top) {
                                    setPackageSelectorAnchorRect({
                                      top: rect.top,
                                      bottom: rect.bottom,
                                      left: rect.left,
                                      right: rect.right,
                                      width: rect.width,
                                      height: rect.height,
                                    });
                                  }
                                }
                              }}
                            >
                              {/* Orange header */}
                              <div
                                style={{
                                  background: '#e8f0fb',
                                  padding: '10px 16px 8px',
                                  borderBottom: '1px solid #dce8f8',
                                }}
                              >
                                <span style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1.5px', color: '#e05a00', textTransform: 'uppercase' }}>
                                  PACKAGE
                                </span>
                              </div>

                              {/* Search row */}
                              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 10px', gap: '10px', background: '#fff', flex: 1 }}>
                                <FaSearch style={{ color: '#9facbc', fontSize: '15px', flexShrink: 0 }} />
                                <input
                                  ref={packageSearchInputRef}
                                  type="text"
                                  placeholder="Search package…"
                                  value={packageSearchTerm}
                                  onChange={(e) => setPackageSearchTerm(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: '16px', fontWeight: 500, color: '#2c3e50', background: 'transparent', minWidth: 0 }}
                                />
                                {packageSearchTerm && (
                                  <button
                                    type="button"
                                    onMouseDown={(e) => { e.stopPropagation(); setPackageSearchTerm(''); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9facbc', fontSize: '13px', padding: '2px', display: 'flex', alignItems: 'center' }}
                                  >
                                    <FaTimes />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Vertical Divider */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Date - Fixed width */}
                          <div
                            className={`field-hover flex-shrink-0 cursor-pointer w-40 px-4 pt-3 pb-2 relative flex flex-col justify-start ${openDatePicker === 'rental-date' ? 'field-hover--active' : ''}`}
                            data-datepicker-anchor="rental-date"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openDatePicker === 'rental-date') {
                                setOpenDatePicker(null);
                                setDatePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              openCalendar('rental-date', e.currentTarget);
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">DATE</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openDatePicker === "rental-date" ? "rotate-180" : ""
                                  }`}
                              />
                            </div>
                            <div className="relative">
                              <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate">
                                {pickupDate ? pickupDate.getDate() : '24'}<span className="text-base sm:text-lg md:text-xl">{pickupDate ? pickupDate.toLocaleString('default', { month: 'short' }) : "Feb"}&apos;{pickupDate ? pickupDate.getFullYear().toString().slice(-2) : '26'}</span>
                              </div>
                              <div className="text-gray-500 text-sm sm:text-base truncate">
                                {pickupDate ? pickupDate.toLocaleString('default', { weekday: 'long' }) : 'Monday'}
                              </div>
                            </div>
                          </div>

                          {/* Vertical Divider */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Pickup Time - Fixed width */}
                          <div
                            className={`field-hover flex-shrink-0 w-40 px-3 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start ${!corporateMember ? 'rounded-r-xl' : ''} ${openTimePicker === 'pickup-rental' ? 'field-hover--active' : ''}`}
                            data-timepicker-anchor="pickup-rental"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openTimePicker === 'pickup-rental') {
                                setOpenTimePicker(null);
                                setTimePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              setSelectedTime(getValidInitialTime(pickupTime, pickupDate));
                              const rect = e.currentTarget.getBoundingClientRect();
                              setOpenTimePicker('pickup-rental');
                              setTimePickerAnchorRect({
                                top: rect.top,
                                bottom: rect.bottom,
                                left: rect.left,
                                right: rect.right,
                                width: rect.width,
                                height: rect.height,
                              });
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">PICKUP TIME</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openTimePicker === "pickup-rental" ? "rotate-180" : ""}`}
                              />
                            </div>
                            <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl whitespace-nowrap flex items-baseline gap-1">
                              <span>{(pickupTime || "10:00 AM").split(" ")[0]}</span>
                              <span className="text-sm font-semibold">{(pickupTime || "10:00 AM").split(" ")[1]}</span>
                            </div>
                          </div>

                          {/* Booking For - Corporate Users Only */}
                          {corporateMember && (
                            <>
                              <div className="w-px self-stretch bg-gray-300"></div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  closeAllPanels();
                                  setBookingForAnchorRect({
                                    top: rect.top,
                                    bottom: rect.bottom,
                                    left: rect.left,
                                    right: rect.right,
                                  });
                                  setIsBookingForModalOpen(!isBookingForModalOpen);
                                }}
                                className={`field-hover flex-shrink-0 w-44 px-3 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start rounded-r-xl ${isBookingForModalOpen ? 'field-hover--active' : ''}`}
                              >
                                <div className="text-gray-400 mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate text-left">
                                  <span className="truncate">BOOKING FOR</span>
                                  <img
                                    src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                    alt="dropdown"
                                    className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`}
                                  />
                                </div>
                                <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? "Guest"
                                    : (selectedBookingFor?.fullName || selectedBookingFor?.name || corporateMember?.fullName || corporateMember?.name || "Corporate User")}
                                </div>
                                <div className="text-gray-500 text-sm truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? ""
                                    : (selectedBookingFor?.email || corporateMember?.email || "")}
                                </div>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Spacer to match Outstation Add More City button gap */}

                    </>
                  ) : (
                    <>
                      {/* Airport - Same Design as Outstation */}
                      <div className="border border-gray-200 rounded-xl bg-white mb-8 sm:mb-10 md:mb-12 w-full" style={{ margin: '0 auto' }}>
                        <div className="flex items-stretch w-full h-[110px]">
                          {/* Airport Selection - display/edit mode like FROM/TO */}
                          {!isEditingAirport ? (
                            <div
                              className="location-box-hover flex-1 min-w-0 pl-4 pr-10 py-3 cursor-pointer flex flex-col justify-center rounded-l-xl relative"
                              style={{ zIndex: 10 }}
                              onClick={() => {
                                closeAllPanels();
                                setIsEditingAirport(true);
                              }}
                            >
                              <div className="font-bold text-gray-400 mb-1 text-xs sm:text-sm tracking-wider uppercase">AIRPORT</div>
                              {(() => {
                                const airportObj = airports.find(a => a.code === selectedAirport || a._id === selectedAirport || a.name === selectedAirport);
                                const displayCity = airportObj ? (airportObj.mainCity?.split(',')[0] || airportObj.name.split(',')[0]) : '';
                                const displayName = airportObj ? airportObj.name : '';
                                return (
                                  <>
                                    <div className="font-bold text-gray-900 truncate text-2xl sm:text-3xl md:text-[34px] leading-normal pb-1">
                                      {displayCity || (selectedAirport && !selectedAirport.match(/^[A-Z]{3}\d*$/i) ? selectedAirport : 'Select Airport')}
                                    </div>
                                    {displayName && (
                                      <div className="text-gray-500 text-sm truncate">
                                        {displayName}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <div
                              className="airport-editing-wrapper flex-1 min-w-0 rounded-l-xl relative"
                              style={{ zIndex: 50, overflow: 'visible' }}
                            >
                              <AirportSelector
                                airports={airports}
                                selectedAirport={selectedAirport}
                                onAirportSelected={(airport) => {
                                  if (airport) {
                                    setSelectedAirport(airport.code);
                                    setPickup(airport.name);
                                    setAirportCity(airport.mainCity);
                                  } else {
                                    setSelectedAirport('');
                                    setPickup('');
                                    setAirportCity('');
                                  }
                                  setIsEditingAirport(false);
                                }}
                                placeholder="Select Airport"
                                autoOpen={true}
                                onClose={() => setIsEditingAirport(false)}
                              />
                            </div>
                          )}

                          {/* Divider with Swap Button */}
                          <div className="relative self-stretch w-0 flex-shrink-0 z-20">
                            {/* Vertical Line - Full height centered behind swap icon */}
                            <div className="w-px h-full bg-gray-300 absolute left-1/2 -translate-x-1/2"></div>
                            {/* Swap Button on top centered */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                              <button
                                type="button"
                                onClick={() => {
                                  setTransferDirection(transferDirection === 'to' ? 'from' : 'to');
                                }}
                                className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center hover:bg-orange-200 transition-colors flex-shrink-0 relative z-10"
                                title="Swap locations"
                              >
                                <img src="https://cdn.builder.io/api/v1/image/assets%2Faa96de3d986f4f74ad1dc4ba97353d1c%2F5ad47c5b66fa44fb9f5f3d4901b88517?format=webp&width=800&height=1200" alt="swap" className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Airport Location - Flex grow */}
                          <div className={`field-hover flex-1 min-w-0 pl-10 pr-4 py-3 relative flex flex-col justify-center ${isEditingTo ? 'field-hover--active' : ''}`} onClick={() => { closeAllPanels(); setIsEditingTo(true); setTimeout(() => document.querySelector('.airport-drop-input input')?.focus(), 0); }} style={{ zIndex: isEditingTo ? 50 : 10, cursor: 'pointer' }}>
                            {!isEditingTo && (
                              <>
                                <div className="text-gray-400 font-bold mb-1 text-xs sm:text-sm tracking-wider uppercase truncate">{transferDirection === 'to' ? 'PICKUP CITY' : 'DROP CITY'}</div>
                                <div className="font-bold text-gray-900 truncate text-2xl sm:text-3xl md:text-[34px] leading-normal pb-1">
                                  {dropoff ? stripPlusCode(dropoff).split(',')[0] : transferDirection === 'to' ? 'PICKUP CITY' : 'DROP CITY'}
                                </div>
                              </>
                            )}
                            {/* Input overlay for autocomplete */}
                            <div className="airport-drop-input" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: isEditingTo ? 9998 : -1, opacity: isEditingTo ? 1 : 0, pointerEvents: isEditingTo ? 'auto' : 'none', visibility: isEditingTo ? 'visible' : 'hidden', overflow: 'visible', transition: 'opacity 0.2s ease' }}>
                              <LocationInput
                                label={transferDirection === 'to' ? 'Pickup City' : 'Drop City'}
                                value={dropoff}
                                onChange={(value) => {
                                  setDropoff(value);
                                  if (!value) setDropoffPlace(null);
                                }}
                                onPlaceSelected={(place) => {
                                  if (place) {
                                    const normalizedPlace = {
                                      ...place,
                                      location:
                                        place.geometry && place.geometry.location
                                          ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() }
                                          : place.location || null,
                                    };
                                    setDropoffPlace(normalizedPlace);
                                    setDropoff(place.formatted_address || place.name || "");
                                    setIsEditingTo(false);
                                  }
                                }}
                                placeholder={transferDirection === 'to' ? 'Enter Pickup City' : 'Enter Drop City'}
                                required
                                country="in"
                              />
                            </div>
                          </div>

                          {/* Vertical Divider */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Date - Fixed width */}
                          <div
                            className={`field-hover flex-shrink-0 cursor-pointer w-36 px-3 pt-3 pb-2 relative flex flex-col justify-start ${openDatePicker === 'airport-date' ? 'field-hover--active' : ''}`}
                            data-datepicker-anchor="airport-date"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openDatePicker === 'airport-date') {
                                setOpenDatePicker(null);
                                setDatePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              openCalendar('airport-date', e.currentTarget);
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">DATE</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openDatePicker === "airport-date" ? "rotate-180" : ""
                                  }`}
                              />
                            </div>
                            <div className="relative">
                              <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate">
                                {pickupDate ? pickupDate.getDate() : '24'}<span className="text-base sm:text-lg md:text-xl">{pickupDate ? pickupDate.toLocaleString('default', { month: 'short' }) : "Feb"}&apos;{pickupDate ? pickupDate.getFullYear().toString().slice(-2) : '26'}</span>
                              </div>
                              <div className="text-gray-500 text-sm sm:text-base truncate">
                                {pickupDate ? pickupDate.toLocaleString('default', { weekday: 'long' }) : 'Monday'}
                              </div>
                            </div>
                          </div>

                          {/* Vertical Divider */}
                          <div className="w-px self-stretch bg-gray-300"></div>

                          {/* Pickup Time - Fixed width */}
                          <div
                            ref={timePickerAnchorRef}
                            className={`field-hover flex-shrink-0 w-44 px-4 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start ${!corporateMember ? 'rounded-r-xl' : ''} ${openTimePicker === 'pickup-airport' ? 'field-hover--active' : ''}`}
                            data-timepicker-anchor="pickup-airport"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openTimePicker === 'pickup-airport') {
                                setOpenTimePicker(null);
                                setTimePickerAnchorRect(null);
                                return;
                              }
                              closeAllPanels();
                              setSelectedTime(getValidInitialTime(pickupTime, pickupDate));
                              const rect = e.currentTarget.getBoundingClientRect();
                              setOpenTimePicker('pickup-airport');
                              setTimePickerAnchorRect({
                                top: rect.top,
                                bottom: rect.bottom,
                                left: rect.left,
                                right: rect.right,
                                width: rect.width,
                                height: rect.height,
                              });
                            }}
                          >
                            <div className="text-gray-400 font-bold mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate">
                              <span className="truncate">PICKUP TIME</span>
                              <img
                                src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                alt="dropdown"
                                className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${openTimePicker === "pickup-airport" ? "rotate-180" : ""}`}
                              />
                            </div>
                            <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl whitespace-nowrap flex items-baseline gap-1">
                              <span>{(pickupTime || "10:00 AM").split(" ")[0]}</span>
                              <span className="text-sm font-semibold">{(pickupTime || "10:00 AM").split(" ")[1]}</span>
                            </div>
                          </div>

                          {/* Booking For - Corporate Users Only */}
                          {corporateMember && (
                            <>
                              <div className="w-px self-stretch bg-gray-300"></div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setBookingForAnchorRect({
                                    top: rect.top,
                                    bottom: rect.bottom,
                                    left: rect.left,
                                    right: rect.right,
                                  });
                                  setIsBookingForModalOpen(!isBookingForModalOpen);
                                }}
                                className={`field-hover flex-shrink-0 w-44 px-3 pt-3 pb-2 relative cursor-pointer flex flex-col justify-start rounded-r-xl ${isBookingForModalOpen ? 'field-hover--active' : ''}`}
                              >
                                <div className="text-gray-400 mb-1 flex items-center gap-1 text-xs sm:text-sm tracking-wider uppercase truncate text-left">
                                  <span className="truncate">BOOKING FOR</span>
                                  <img
                                    src={corporateMember ? "/icons/drop-orange.svg" : "/icons/drop.svg"}
                                    alt="dropdown"
                                    className={`w-3 h-3 object-contain flex-shrink-0 transition-transform ${isBookingForModalOpen ? 'rotate-180' : ''}`}
                                  />
                                </div>
                                <div className="font-bold text-gray-900 text-xl sm:text-2xl md:text-3xl truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? "Guest"
                                    : (selectedBookingFor?.fullName || selectedBookingFor?.name || corporateMember?.fullName || corporateMember?.name || "Corporate User")}
                                </div>
                                <div className="text-gray-500 text-sm truncate text-left">
                                  {selectedBookingFor?.isGuest
                                    ? ""
                                    : (selectedBookingFor?.email || corporateMember?.email || "")}
                                </div>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Spacer to match Outstation Add More City button gap */}
                      <div className="h-10 sm:h-12 md:h-14"></div>
                    </>
                  )}
                </div>

                {/* Add More City Button - Only for outstation journeys */}
                {journeyType === "outstation" && (
                  <div className="add-city-btn-wrapper">
                    <button
                      type="button"
                      className={`add-city-btn bg-gradient-to-r ${corporateMember ? 'from-[#F56753] to-[#D94E3A] hover:from-[#6b1818] hover:to-[#4a0f0f]' : 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'} text-white flex items-center text-sm font-medium transition-all duration-200 hover:scale-105 px-4 py-2 rounded-full shadow-md`}
                      onClick={() => {
                        const newCityId = cityCounter + 1;
                        setAdditionalCities([
                          ...additionalCities,
                          { id: newCityId, value: "" },
                        ]);
                        setCityCounter(newCityId);
                        setMultiCity(true);
                      }}
                    >
                      <FaPlus className="mr-2 text-base" /> Add More City
                    </button>
                  </div>
                )}

                {/* Additional City Fields and Mobile Number */}
                {journeyType === "outstation" && additionalCities.length > 0 && (
                  <div className="flex flex-wrap items-end gap-2">
                    {additionalCities.map((city, index) => (
                      <div key={city.id} className="relative group">
                        <div className="flex items-center gap-2">
                          <div className="relative w-full sm:max-w-xs">
                            <LocationInput
                              label={`Via City ${index + 1}`}
                              value={city.value}
                              onChange={(newValue) => {
                                const updatedCities = [...additionalCities];
                                updatedCities[index].value = newValue;
                                setAdditionalCities(updatedCities);
                              }}
                              onPlaceSelected={(place) => {
                                if (place) {
                                  const updatedCities = [...additionalCities];
                                  updatedCities[index].value = place.formatted_address || place.name || "";
                                  setAdditionalCities(updatedCities);
                                }
                              }}
                              placeholder="Enter via city"
                              types={['(cities)']}
                            />
                          </div>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 mt-5"
                            onClick={() => {
                              const updatedCities = additionalCities.filter(
                                (_, i) => i !== index
                              );
                              setAdditionalCities(updatedCities);
                              if (updatedCities.length === 0) {
                                setMultiCity(false);
                              }
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Return Date/Time Section - Removed for Round Trip, now in main row */}
                {/* Error Message */}
                {errorMessage && (
                  <div className="text-red-600 text-base p-4 bg-red-50 rounded-lg border border-red-200 font-medium">
                    {errorMessage}
                  </div>
                )}

                {openDatePicker && typeof document !== 'undefined' && createPortal(
                  <div
                    data-datepicker-portal="true"
                    style={{
                      position: 'absolute',
                      top: (typeof window !== 'undefined' ? getDatePickerPosition().top : 0),
                      left: (typeof window !== 'undefined' ? getDatePickerPosition().left : 0),
                      zIndex: 40,
                    }}
                    ref={(el) => {
                      datePickerPortalRef.current = el;
                      if (!el) return;
                      const rect = el.getBoundingClientRect();
                      const next = {
                        width: Math.round(rect.width),
                        height: Math.round(rect.height),
                      };
                      setDatePickerSize((prev) => {
                        if (prev.width === next.width && prev.height === next.height) return prev;
                        return next;
                      });
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ModernDatePicker
                      selected={
                        openDatePicker === 'return'
                          ? returnDate
                          : pickupDate
                      }
                      onChange={(date) => {
                        if (openDatePicker === 'return') {
                          setReturnDate(date);
                        } else {
                          setPickupDate(date);
                        }
                        setOpenDatePicker(null);
                        setDatePickerAnchorRect(null);
                      }}
                      minDate={
                        openDatePicker === 'return'
                          ? (pickupDate || new Date())
                          : new Date()
                      }
                    />
                  </div>,
                  document.body
                )}

                {openTimePicker && typeof document !== 'undefined' && createPortal(
                  <div
                    ref={timePickerPortalRef}
                    style={{
                      position: 'absolute',
                      top: (typeof window !== 'undefined' ? getTimePickerPosition().top : 0),
                      left: (typeof window !== 'undefined' ? getTimePickerPosition().left : 0),
                      zIndex: 40,
                      background: 'white',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      padding: '10px',
                      width: '280px',
                      maxWidth: 'calc(100vw - 24px)',
                      boxSizing: 'border-box',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ textAlign: 'center', marginBottom: '8px', fontWeight: 600, fontSize: '13px', color: '#374151', textTransform: 'uppercase' }}>
                      {openTimePicker.startsWith('pickup') ? 'PICKUP TIME' : (openTimePicker === 'drop' ? 'DROP TIME' : 'Select Time')}
                    </div>

                    {/* Time Display */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '4px',
                      marginBottom: '10px',
                      padding: '8px',
                      background: '#f3f4f6',
                      borderRadius: 6,
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                        {String(selectedTime.getHours() % 12 || 12).padStart(2, '0')}
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>:</div>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                        {String(selectedTime.getMinutes()).padStart(2, '0')}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: '4px',
                        gap: '1px',
                      }}>
                        <button
                          type="button"
                          disabled={isAMDisabled}
                          onClick={() => {
                            if (isAMDisabled) return;
                            const newDate = new Date(selectedTime);
                            if (selectedTime.getHours() >= 12) {
                              newDate.setHours(selectedTime.getHours() - 12);
                            }
                            newDate.setSeconds(0, 0);
                            setSelectedTime(ensureFutureTime(newDate));
                          }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            fontWeight: 600,
                            borderRadius: 3,
                            border: 'none',
                            background: selectedTime.getHours() < 12 ? '#3b82f6' : '#e5e7eb',
                            color: selectedTime.getHours() < 12 ? 'white' : '#6b7280',
                            cursor: isAMDisabled ? 'not-allowed' : 'pointer',
                            opacity: isAMDisabled ? 0.5 : 1,
                          }}
                        >
                          AM
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newDate = new Date(selectedTime);
                            if (selectedTime.getHours() < 12) {
                              newDate.setHours(selectedTime.getHours() + 12);
                            }
                            newDate.setSeconds(0, 0);
                            setSelectedTime(ensureFutureTime(newDate));
                          }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            fontWeight: 600,
                            borderRadius: 3,
                            border: 'none',
                            background: selectedTime.getHours() >= 12 ? '#3b82f6' : '#e5e7eb',
                            color: selectedTime.getHours() >= 12 ? 'white' : '#6b7280',
                            cursor: 'pointer',
                          }}
                        >
                          PM
                        </button>
                      </div>
                    </div>

                    {/* Hours and Minutes Rows */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* Hours Column */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px', textAlign: 'center' }}>Hours</div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: '3px',
                        }}>
                          {[...Array(12)].map((_, i) => {
                            const hour = i + 1;
                            const isSelected = (selectedTime.getHours() % 12 || 12) === hour;
                            const isDisabled = isHourDisabled(hour);
                            return (
                              <button
                                key={hour}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => {
                                  if (isDisabled) return;
                                  const newDate = new Date(selectedTime);
                                  const currentIsPM = selectedTime.getHours() >= 12;
                                  const newHour = getHour24(hour, currentIsPM);
                                  newDate.setHours(newHour);
                                  newDate.setSeconds(0, 0);
                                  setSelectedTime(ensureFutureTime(newDate));
                                }}
                                style={{
                                  padding: '5px 2px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  borderRadius: 4,
                                  border: 'none',
                                  background: isSelected ? '#3b82f6' : '#f3f4f6',
                                  color: isSelected ? 'white' : '#374151',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  opacity: isDisabled ? 0.5 : 1,
                                }}
                              >
                                {String(hour).padStart(2, '0')}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Minutes Column */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px', textAlign: 'center' }}>Minutes</div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: '3px',
                        }}>
                          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((min) => {
                            const isSelected = selectedTime.getMinutes() === min;
                            const isDisabled = isMinuteDisabled(min);
                            return (
                              <button
                                key={min}
                                type="button"
                                disabled={isDisabled}
                                onClick={() => {
                                  if (isDisabled) return;
                                  const newDate = new Date(selectedTime);
                                  newDate.setMinutes(min, 0, 0);
                                  setSelectedTime(ensureFutureTime(newDate));
                                }}
                                style={{
                                  padding: '5px 2px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  borderRadius: 4,
                                  border: 'none',
                                  background: isSelected ? '#3b82f6' : '#f3f4f6',
                                  color: isSelected ? 'white' : '#374151',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  opacity: isDisabled ? 0.5 : 1,
                                }}
                              >
                                {String(min).padStart(2, '0')}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpenTimePicker(null);
                          setTimePickerAnchorRect(null);
                        }}
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: '#f3f4f6',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: '#374151',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const hours = selectedTime.getHours();
                          const minutes = selectedTime.getMinutes();
                          const ampm = hours >= 12 ? 'PM' : 'AM';
                          const displayHours = hours % 12 || 12;
                          let time12 = `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;

                          // Validate: if today, don't allow past time for pickup
                          const isPickupToday = openTimePicker && openTimePicker.startsWith('pickup') && (typeof isToday === 'function' ? isToday(pickupDate) : (pickupDate && new Date(pickupDate).toDateString() === new Date().toDateString()));
                          if (isPickupToday) {
                            const now = new Date();
                            const currentTotalMins = now.getHours() * 60 + now.getMinutes();
                            const selectedTotalMins = hours * 60 + minutes;
                            if (selectedTotalMins < currentTotalMins) {
                              // Auto-correct to current time
                              const nowH = now.getHours();
                              const nowM = now.getMinutes();
                              const nowAmpm = nowH >= 12 ? 'PM' : 'AM';
                              const nowDisplayH = nowH % 12 || 12;
                              time12 = `${nowDisplayH}:${String(nowM).padStart(2, '0')} ${nowAmpm}`;
                              toast.warn('⏰ Cannot select a past time. Time adjusted to current time.', { autoClose: 3500 });
                            }
                          }

                          if (openTimePicker === 'drop') {
                            setReturnTime(time12);
                          } else {
                            setPickupTime(time12);
                          }
                          setOpenTimePicker(null);
                          setTimePickerAnchorRect(null);
                        }}
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: '#3b82f6',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '12px',
                          color: 'white',
                        }}
                      >
                        OK
                      </button>
                    </div>
                  </div>,
                  document.body
                )}

                {/* Submit Button - Centered with proper spacing */}
                <div className="absolute left-1/2 -bottom-6 -translate-x-1/2 z-30 flex justify-center w-full">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`
                      flex items-center gap-3
                      bg-gradient-to-r ${corporateMember ? 'from-[#F56753] to-[#D94E3A] hover:from-[#6b1818] hover:to-[#4a0f0f]' : 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'}
                      text-white font-semibold text-lg
                      px-10 py-2
                      rounded-full
                      shadow-xl
                      hover:shadow-2xl
                      transition-all duration-300
                      active:scale-95
                      disabled:opacity-60 disabled:cursor-not-allowed
                      ${corporateMember ? 'book-ride-btn-animate-corporate' : 'book-ride-btn-animate'}
                    `}
                  >
                    {/* ICON */}
                    <div className="bg-white/10 p-2 rounded-full">
                      <img
                        src="https://cdn.builder.io/api/v1/image/assets%2F4705fbdd6ff74f00a61419cf1bf2c4bc%2F795e362fac4b42d493fc0776c9d26213?format=webp&width=800&height=1200"
                        alt="Book Ride"
                        className="w-5 h-5 object-contain"
                      />
                    </div>

                    {/* TEXT */}
                    <span>
                      {isLoading ? "Searching..." : "Book Ride"}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Desktop Booking For Modal */}
      {isBookingForModalOpen && corporateMember && createPortal(
        <div
          data-booking-for-modal="true"
          className="hidden md:block"
          style={{
            position: 'fixed',
            top: (typeof window !== 'undefined' ? getBookingForModalPosition().top : 0),
            left: (typeof window !== 'undefined' ? getBookingForModalPosition().left : 0),
            zIndex: 2147483647,
            pointerEvents: 'none',
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-80 max-h-[450px] flex flex-col overflow-hidden border border-gray-100"
            onClick={(e) => e.stopPropagation()}
            style={{ pointerEvents: 'auto' }}
          >
            {/* Header */}
            <div className="bg-white px-5 py-4 border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F56753]" />
                <h3 className="text-[17px] font-bold text-gray-900">Booking for</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsBookingForModalOpen(false)}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={14} />
              </button>
            </div>
            {/* Content */}
            {renderBookingForModalContent()}
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Booking For Bottom Sheet */}
      {isBookingForModalOpen && corporateMember && createPortal(
        <div
          data-booking-for-modal="true"
          className="fixed inset-0 z-[1000] flex flex-col justify-end bg-black/50 md:hidden"
          onClick={() => setIsBookingForModalOpen(false)}
        >
          <div
            className="bg-white rounded-t-2xl flex flex-col max-h-[80vh] w-full animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle/indicator at top center */}
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-3 flex-shrink-0"></div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F56753]" />
                <h3 className="text-[17px] font-bold text-gray-900">Booking for</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsBookingForModalOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={16} />
              </button>
            </div>

            {/* List and Search Content */}
            {renderBookingForModalContent()}
            
            {/* Bottom padding for mobile safe area */}
            <div className="h-6 bg-white flex-shrink-0"></div>
          </div>
        </div>,
        document.body
      )}

      {/* Portal for Package Selector — LocationInput style */}
      {openPackageSelector && packageSelectorAnchorRect && createPortal(
        <div
          data-package-portal="true"
          style={{
            position: 'absolute',
            top: getPackageSelectorPosition().top,
            left: getPackageSelectorPosition().left,
            width: Math.max(getPackageSelectorPosition().width, 300),
            zIndex: 40,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* LocationInput-style panel */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderTop: '2px solid #e05a00',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              borderRadius: '0 0 10px 10px',
              overflow: 'hidden',
            }}
          >
            {/* Package list — No header/search here since it's now inline */}
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {(() => {
                const filtered = rentalPackages.filter((pkg) => {
                  if (localCity) {
                    if (!pkg.serviceCities || !Array.isArray(pkg.serviceCities) || pkg.serviceCities.length === 0) return false;
                    const cityMatch = pkg.serviceCities.some((sc) => {
                      const cityName = sc?.cityName || sc?.name || sc;
                      if (!cityName) return false;
                      return cityName.toString().toLowerCase().trim() === localCity.toLowerCase().trim();
                    });
                    if (!cityMatch) return false;
                  }

                  if (packageSearchTerm.trim()) {
                    const term = packageSearchTerm.toLowerCase();
                    return pkg.name.toLowerCase().includes(term) ||
                      (pkg.duration || pkg.hr || "").toString().toLowerCase().includes(term) ||
                      (pkg.distance || pkg.km || "").toString().toLowerCase().includes(term);
                  }

                  return true;
                });

                if (filtered.length === 0) {
                  return (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9facbc' }}>
                      {localCity ? `No packages for ${localCity}` : 'No packages available'}
                    </div>
                  );
                }

                return (
                  <>
                    {/* Package list */}

                    {filtered.map((pkg) => (
                      <div
                        key={pkg._id}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setHourlyPackage(pkg._id);
                          setOpenPackageSelector(false);
                          setIsEditingPackage(false);
                          setPackageSearchTerm('');
                        }}
                        className={`location-input-cityItem ${hourlyPackage === pkg._id ? 'location-input-cityItem--active' : ''}`}
                      >
                        <div style={{ fontWeight: 600 }}>{pkg.name}</div>
                        {((pkg.duration || pkg.hr) || (pkg.distance || pkg.km)) && (
                          <div style={{ fontSize: '13px', color: '#9facbc', marginTop: '2px' }}>
                            {pkg.duration || pkg.hr || ''} hrs / {pkg.distance || pkg.km || ''} kms
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => {
          setIsLoginModalOpen(false);
          localStorage.removeItem("pendingSubmitAfterLogin");
        }}
        onSuccess={handleLoginSuccess}
      />

      {/* Mobile Search Modal Overlay */}
      {mobileSearchModal.isOpen && createPortal(
        <div className="mobile-search-modal-overlay">
          <div className="mobile-search-modal-content">
            {/* Header with Back Button */}
            <div className="mobile-search-header">
              <button
                type="button"
                className="mobile-search-back-btn"
                onClick={handleMobileSearchClose}
              >
                <FaArrowRight className="rotate-180" />
              </button>

              <div className="mobile-search-inputs-container">
                {mobileSearchModal.type === 'package' ? (
                  <div className="mobile-search-input-wrapper active">
                    <label className="mobile-search-input-label">SELECT PACKAGE</label>
                    <div className="flex items-center gap-2 w-full pr-3">
                      <FaSearch className="text-gray-400 text-[13px] flex-shrink-0" />
                      <input
                        type="text"
                        className="w-full bg-transparent border-none outline-none text-base font-bold text-gray-900 placeholder-gray-400"
                        placeholder="Search packages (e.g. 8 hr, 80 km)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {searchQuery && (
                        <button type="button" onClick={() => setSearchQuery('')} className="text-gray-400 p-1 flex items-center justify-center">
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : mobileSearchModal.type === 'city' ? (
                  <div className="mobile-search-input-wrapper active">
                    <label className="mobile-search-input-label">CITY</label>
                    <div className="flex items-center gap-2 w-full pr-3">
                      <FaSearch className="text-gray-400 text-[13px] flex-shrink-0" />
                      <input
                        type="text"
                        className="w-full bg-transparent border-none outline-none text-base font-bold text-gray-900 placeholder-gray-400"
                        placeholder="Search City..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {searchQuery && (
                        <button type="button" onClick={() => setSearchQuery('')} className="text-gray-400 p-1 flex items-center justify-center">
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : mobileSearchModal.type === 'airport' ? (
                  <div className="mobile-search-input-wrapper active">
                    <label className="mobile-search-input-label">AIRPORT</label>
                    <div className="flex items-center gap-2 w-full pr-3">
                      <FaSearch className="text-gray-400 text-[13px] flex-shrink-0" />
                      <input
                        type="text"
                        className="w-full bg-transparent border-none outline-none text-base font-bold text-gray-900 placeholder-gray-400"
                        placeholder="Search by code, name or city..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      {searchQuery && (
                        <button type="button" onClick={() => setSearchQuery('')} className="text-gray-400 p-1 flex items-center justify-center">
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ) : mobileSearchModal.type === 'airport-city' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                    {/* Row 1 — Airport (tappable to change) */}
                    <div
                      onClick={() => { setSearchQuery(''); setMobileSearchModal({ isOpen: true, type: 'airport' }); }}
                      style={{
                        background: '#f0f7ff',
                        border: '1.5px solid #d0e6ff',
                        borderRadius: '8px',
                        padding: '5px 12px',
                        marginBottom: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <label style={{ fontSize: '10px', fontWeight: 800, color: '#3b82f6', letterSpacing: '0.5px', cursor: 'pointer' }}>AIRPORT</label>
                        <span style={{ fontSize: '9px', fontWeight: 800, color: '#3b82f6', background: '#dbeafe', borderRadius: '4px', padding: '1px 6px', letterSpacing: '0.5px' }}>CHANGE</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <FaPlane style={{ color: '#3b82f6', fontSize: '12px', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                          {selectedAirport
                            ? (() => { const ap = airports.find(a => a.code === selectedAirport); return ap ? `${ap.code} — ${ap.name}` : selectedAirport; })()
                            : 'Select Airport'}
                        </span>
                      </div>
                    </div>
                    {/* Row 2 — Pickup / Drop City via Google Places */}
                    <div className="mobile-search-input-wrapper active" style={{ marginBottom: 0, minWidth: 0, overflow: 'hidden' }}>
                      <label className="mobile-search-input-label">{transferDirection === 'to' ? 'PICKUP CITY' : 'DROP CITY'}</label>
                      <LocationInput
                        ref={mobileAirportCityRef}
                        id="mobile-search-airport-city"
                        placeholder={transferDirection === 'to' ? 'Search pickup city...' : 'Search drop city...'}
                        value={dropoff}
                        onChange={(val) => setDropoff(val)}
                        onPlaceSelected={(place) => {
                          const loc = place.formatted_address || place.name || '';
                          setDropoff(loc);
                          setDropoffPlace(place);
                          handleMobileSearchClose();
                        }}
                        autoFocus={true}
                        hideHeader={true}
                        hideDropdown={true}
                        onSuggestions={setMobileSuggestions}
                      />
                    </div>
                  </div>
                ) : mobileSearchModal.type.startsWith('stop-') ? (
                  <div className="mobile-search-input-wrapper active">
                    <label className="mobile-search-input-label">STOP {parseInt(mobileSearchModal.type.split('-')[1]) + 1}</label>
                    <LocationInput
                      ref={mobileStopRef}
                      id="mobile-search-stop"
                      placeholder="Enter Stop location"
                      value={additionalCities[parseInt(mobileSearchModal.type.split('-')[1])]?.value || ""}
                      onChange={(val) => {
                        const idx = parseInt(mobileSearchModal.type.split('-')[1]);
                        const newCities = [...additionalCities];
                        newCities[idx] = { ...newCities[idx], value: val };
                        setAdditionalCities(newCities);
                      }}
                      onPlaceSelected={(place) => {
                        const loc = place.formatted_address || place.name || "";
                        const idx = parseInt(mobileSearchModal.type.split('-')[1]);
                        const newCities = [...additionalCities];
                        newCities[idx] = { ...newCities[idx], value: loc };
                        setAdditionalCities(newCities);
                        handleMobileSearchClose();
                      }}
                      autoFocus={true}
                      hideHeader={true}
                      hideDropdown={true}
                      onSuggestions={setMobileSuggestions}
                    />
                  </div>
                ) : (
                  <>
                    <div className={`mobile-search-input-wrapper ${mobileSearchModal.type === 'from' ? 'active' : ''}`}>
                      <label className="mobile-search-input-label">
                        {journeyType === 'airport' 
                          ? (transferDirection === 'to' ? 'PICKUP CITY' : 'FROM AIRPORT')
                          : 'FROM'}
                      </label>
                      <LocationInput
                        ref={mobileFromRef}
                        id="mobile-search-from"
                        placeholder={journeyType === 'airport' && transferDirection === 'from' ? "Selected Airport" : "Enter Pick up location"}
                        value={tempPickup}
                        onChange={(val) => setTempPickup(val)}
                        onPlaceSelected={(place) => {
                          const loc = place.formatted_address || place.name;
                          setPickup(loc);
                          setPickupPlace(place);
                          setTempPickup(loc);
                          if (mobileSearchModal.type === 'from') {
                            setMobileSearchModal(prev => ({ ...prev, type: 'to' }));
                          } else {
                            handleMobileSearchClose();
                          }
                        }}
                        autoFocus={mobileSearchModal.type === 'from'}
                        hideHeader={true}
                        readOnly={journeyType === 'airport' && transferDirection === 'from'}
                        hideDropdown={true}
                        onSuggestions={setMobileSuggestions}
                      />
                    </div>

                    <div className="mobile-search-divider">
                      <div className="mobile-search-swap-btn" onClick={() => {
                        if (journeyType === 'airport') {
                          setTransferDirection(transferDirection === 'to' ? 'from' : 'to');
                          return;
                        }
                        const tPickup = tempPickup;
                        const tDropoff = tempDropoff;
                        setTempPickup(tDropoff);
                        setTempDropoff(tPickup);
                        setPickup(tDropoff);
                        setDropoff(tPickup);
                        const tPlace = pickupPlace;
                        setPickupPlace(dropoffPlace);
                        setDropoffPlace(tPlace);
                      }}>
                        <FaExchangeAlt className="rotate-90" />
                      </div>
                    </div>

                    <div className={`mobile-search-input-wrapper ${mobileSearchModal.type === 'to' ? 'active' : ''}`}>
                      <label className="mobile-search-input-label">
                        {journeyType === 'airport' 
                          ? (transferDirection === 'to' ? 'TO AIRPORT' : 'DROP CITY')
                          : 'TO'}
                      </label>
                      <LocationInput
                        ref={mobileToRef}
                        id="mobile-search-to"
                        placeholder={journeyType === 'airport' && transferDirection === 'to' ? "Selected Airport" : "Enter Drop location"}
                        value={tempDropoff}
                        onChange={(val) => setTempDropoff(val)}
                        onPlaceSelected={(place) => {
                          const loc = place.formatted_address || place.name;
                          setDropoff(loc);
                          setDropoffPlace(place);
                          setTempDropoff(loc);
                          if (mobileSearchModal.type === 'to') {
                            handleMobileSearchClose();
                          } else {
                            setMobileSearchModal(prev => ({ ...prev, type: 'from' }));
                          }
                        }}
                        autoFocus={mobileSearchModal.type === 'to'}
                        hideHeader={true}
                        readOnly={journeyType === 'airport' && transferDirection === 'to'}
                        hideDropdown={true}
                        onSuggestions={setMobileSuggestions}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Suggestions list */}
            <div className="mobile-search-body">
              {mobileSuggestions.length > 0 ? (
                <div className="mobile-search-section">
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaSearch size={12} /> SUGGESTIONS
                  </div>
                  <div className="mobile-search-list">
                    {mobileSuggestions.map((item, idx) => (
                      <div
                        key={item.place_id || idx}
                        className="mobile-search-list-item flex flex-col"
                        onClick={() => {
                          if (mobileSearchModal.type === 'from') {
                            mobileFromRef.current?.selectSuggestion(item);
                          } else if (mobileSearchModal.type === 'to') {
                            mobileToRef.current?.selectSuggestion(item);
                          } else if (mobileSearchModal.type === 'airport-city') {
                            mobileAirportCityRef.current?.selectSuggestion(item);
                          } else if (mobileSearchModal.type.startsWith('stop-')) {
                            mobileStopRef.current?.selectSuggestion(item);
                          }
                        }}
                      >
                        <span className="font-normal text-gray-800 text-sm leading-snug">
                          {stripPlusCode(item.description || (item.structured_formatting ? `${item.structured_formatting.main_text}, ${item.structured_formatting.secondary_text}` : item.name))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : mobileSearchModal.type === 'package' ? (
                <div className="mobile-search-section">
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaClock size={12} /> AVAILABLE PACKAGES IN {localCity?.toUpperCase() || 'CITY'}
                  </div>
                  <div className="mobile-search-list">
                    {rentalPackages
                      .filter((pkg) => {
                        if (localCity) {
                          if (!pkg.serviceCities || !Array.isArray(pkg.serviceCities) || pkg.serviceCities.length === 0) return false;
                          const cityMatch = pkg.serviceCities.some((sc) => {
                            const cityName = sc?.cityName || sc?.name || sc;
                            if (!cityName) return false;
                            return cityName.toString().toLowerCase().trim() === localCity.toLowerCase().trim();
                          });
                          if (!cityMatch) return false;
                        }

                        if (searchQuery.trim()) {
                          const term = searchQuery.toLowerCase();
                          return pkg.name.toLowerCase().includes(term) ||
                            (pkg.description || "").toLowerCase().includes(term) ||
                            (pkg.duration || pkg.hr || "").toString().toLowerCase().includes(term) ||
                            (pkg.distance || pkg.km || "").toString().toLowerCase().includes(term);
                        }

                        return true;
                      })
                      .map((pkg) => (
                        <div
                          key={pkg._id}
                          className={`mobile-search-list-item flex flex-col ${hourlyPackage === pkg._id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                          onClick={() => {
                            setHourlyPackage(pkg._id);
                            handleMobileSearchClose();
                          }}
                        >
                          <span className="font-bold text-gray-900">{pkg.name}</span>
                          {pkg.description && (
                            <span className="text-sm text-gray-500 mt-1">{pkg.description}</span>
                          )}
                        </div>
                      ))}
                    {rentalPackages.filter((pkg) => {
                      if (localCity) {
                        if (!pkg.serviceCities || !Array.isArray(pkg.serviceCities) || pkg.serviceCities.length === 0) return false;
                        const cityMatch = pkg.serviceCities.some((sc) => {
                          const cityName = sc?.cityName || sc?.name || sc;
                          if (!cityName) return false;
                          return cityName.toString().toLowerCase().trim() === localCity.toLowerCase().trim();
                        });
                        if (!cityMatch) return false;
                      }
                      if (searchQuery.trim()) {
                        const term = searchQuery.toLowerCase();
                        return pkg.name.toLowerCase().includes(term) ||
                          (pkg.description || "").toLowerCase().includes(term) ||
                          (pkg.duration || pkg.hr || "").toString().toLowerCase().includes(term) ||
                          (pkg.distance || pkg.km || "").toString().toLowerCase().includes(term);
                      }
                      return true;
                    }).length === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        {localCity ? `No packages found for ${localCity}` : 'No packages found'}
                      </div>
                    )}
                  </div>
                </div>
              ) : mobileSearchModal.type === 'airport' ? (
                <div className="mobile-search-section">
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaSearch size={12} /> AIRPORTS
                  </div>
                  <div className="mobile-search-list">
                    {airports
                      ?.filter(airport => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return (
                          (airport?.code && airport.code.toLowerCase().includes(q)) ||
                          (airport?.name && airport.name.toLowerCase().includes(q)) ||
                          (airport?.mainCity && airport.mainCity.toLowerCase().includes(q)) ||
                          (airport?.state && airport.state.toLowerCase().includes(q))
                        );
                      })
                      .map(airport => (
                        <div
                          key={airport.code}
                          className="mobile-search-list-item flex flex-col"
                          onClick={() => {
                            setSelectedAirport(airport.code);
                            setPickup(airport.code);
                            setAirportCity(airport.mainCity);
                            // Auto-advance to city picker
                            setSearchQuery('');
                            setMobileSearchModal({ isOpen: true, type: 'airport-city' });
                          }}
                        >
                          <span className="font-bold text-gray-900">{airport.code} — {airport.name}</span>
                          <span className="text-sm text-gray-500">{airport.mainCity}, {airport.state}</span>
                        </div>
                      ))}
                    {airports.filter(airport => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return (
                        (airport.code && airport.code.toLowerCase().includes(q)) ||
                        (airport.name && airport.name.toLowerCase().includes(q)) ||
                        (airport.mainCity && airport.mainCity.toLowerCase().includes(q)) ||
                        (airport.state && airport.state.toLowerCase().includes(q))
                      );
                    }).length === 0 && (
                      <div className="p-4 text-center text-gray-500">No airports found</div>
                    )}
                  </div>
                </div>
              ) : mobileSearchModal.type === 'airport-city' ? (
                <div className="mobile-search-section">
                  {/* Selected Airport Info Banner */}
                  {selectedAirport && (() => { const ap = airports.find(a => a.code === selectedAirport); return ap ? (
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaPlane style={{ color: '#3b82f6', fontSize: '14px', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#3b82f6', letterSpacing: '0.5px', marginBottom: '2px' }}>AIRPORT</div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a5f' }}>{ap.code} — {ap.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{ap.mainCity}, {ap.state}</div>
                      </div>
                    </div>
                  ) : null; })()}
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaSearch size={12} /> {transferDirection === 'to' ? 'QUICK PICK — PICKUP CITIES' : 'QUICK PICK — DROP CITIES'}
                  </div>
                  {airportServiceCities.length > 0 ? (
                    <div className="mobile-search-list">
                      {airportServiceCities.map(city => (
                        <div
                          key={city._id || city.name}
                          className="mobile-search-list-item flex flex-col"
                          onClick={() => {
                            setDropoff(city.name);
                            handleMobileSearchClose();
                          }}
                        >
                          <span className="font-bold text-gray-900">{city.name}</span>
                          <span className="text-sm text-gray-500">Service City</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      {selectedAirport ? 'No service cities found. Search any city above.' : 'Please select an airport first.'}
                    </div>
                  )}
                </div>
              ) : mobileSearchModal.type === 'city' ? (
                <div className="mobile-search-section">
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaSearch size={12} /> CITIES
                  </div>
                  <div className="mobile-search-list">
                    {[...new Set(rentalPackages
                      .filter(pkg => pkg.serviceCities && Array.isArray(pkg.serviceCities) && pkg.serviceCities.length > 0)
                      .flatMap(pkg => pkg.serviceCities.map(sc => sc.cityName || sc.name || sc))
                      .filter(Boolean)
                    )]
                      .sort()
                      .filter(city => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return city.toLowerCase().includes(q);
                      })
                      .map(city => (
                        <div
                          key={city}
                          className="mobile-search-list-item flex flex-col"
                          onClick={() => {
                            setLocalCity(city);
                            handleMobileSearchClose();
                          }}
                        >
                          <span className="font-bold text-gray-900">{city}</span>
                          <span className="text-sm text-gray-500">Rental City</span>
                        </div>
                      ))}
                    {[...new Set(rentalPackages
                      .filter(pkg => pkg.serviceCities && Array.isArray(pkg.serviceCities) && pkg.serviceCities.length > 0)
                      .flatMap(pkg => pkg.serviceCities.map(sc => sc.cityName || sc.name || sc))
                      .filter(Boolean)
                    )]
                      .filter(city => {
                        if (!searchQuery) return true;
                        const q = searchQuery.toLowerCase();
                        return city.toLowerCase().includes(q);
                      }).length === 0 && (
                      <div className="p-4 text-center text-gray-500">No cities found</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mobile-search-section">
                  <div className="mobile-search-section-title flex items-center gap-2">
                    <FaClock size={12} /> RECENT SEARCHES
                  </div>
                  <div className="mobile-search-list">
                    {recentSearches.map((item, idx) => {
                      const fullAddr = stripPlusCode(item.description || item.name || (item.city ? `${item.city}, ${item.sub}` : ''));
                      return (
                        <div
                          key={item.id || idx}
                          className="mobile-search-list-item flex flex-col"
                          onClick={() => {
                            if (mobileSearchModal.type === 'from') {
                              setPickup(fullAddr);
                              setTempPickup(fullAddr);
                              setMobileSearchModal(prev => ({ ...prev, type: 'to' }));
                            } else if (mobileSearchModal.type === 'to') {
                              setDropoff(fullAddr);
                              setTempDropoff(fullAddr);
                              handleMobileSearchClose();
                            } else if (mobileSearchModal.type.startsWith('stop-')) {
                              const sIdx = parseInt(mobileSearchModal.type.split('-')[1]);
                              const newCities = [...additionalCities];
                              newCities[sIdx] = { ...newCities[sIdx], value: fullAddr };
                              setAdditionalCities(newCities);
                              handleMobileSearchClose();
                            } else if (mobileSearchModal.type === 'city') {
                              setLocalCity(fullAddr.split(',')[0]);
                              handleMobileSearchClose();
                            }
                          }}
                        >
                          <span className="font-normal text-gray-800 text-sm leading-snug">
                            {fullAddr}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Date Time Sheet */}
      <MobileDateTimeSheet
        isOpen={mobileDateTimeSheet.isOpen}
        onClose={() => setMobileDateTimeSheet({ ...mobileDateTimeSheet, isOpen: false })}
        type={mobileDateTimeSheet.type}
        onDone={handleMobileDateTimeDone}
        pickupDate={pickupDate}
        returnDate={returnDate}
        pickupTime={pickupTime}
        returnTime={returnTime}
        tripType={tripType}
        setTripType={setTripType}
        journeyType={journeyType}
        corporateMember={corporateMember}
      />
    </section>
  );
};

const MobileDateTimeSheet = ({ isOpen, onClose, type, onDone, pickupDate, returnDate, pickupTime, returnTime, tripType, setTripType, journeyType, corporateMember }) => {
  const [activeTab, setActiveTab] = useState(type);
  const [selectedDate, setSelectedDate] = useState(type === 'start' ? pickupDate : returnDate);
  const [localTripType, setLocalTripType] = useState(tripType);
  
  // Sync tab with prop when sheet opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(type);
      setSelectedDate(type === 'start' ? pickupDate : returnDate);
      setLocalTripType(tripType);
    }
  }, [isOpen, type, pickupDate, returnDate, tripType]);

  // Calculate initial scroller positions
  const getInitialTime = (currentType) => {
    const isStart = currentType === 'start';
    const timeToParse = isStart ? pickupTime : returnTime;
    const dateToCheck = isStart ? pickupDate : returnDate;
    const now = new Date();
    const isToday = dateToCheck.toDateString() === now.toDateString();

    // ONLY use real-time for START trip today
    if (isStart && (isToday || !timeToParse)) {
      let h = now.getHours();
      const p = h >= 12 ? "PM" : "AM";
      h = h % 12;
      h = h ? h : 12;
      const m = Math.round(now.getMinutes() / 5) * 5;
      const mStr = (m >= 60 ? 0 : m).toString().padStart(2, '0');
      const finalH = m >= 60 ? (h % 12 + 1 || 1) : h;
      return { h: finalH > 12 ? (finalH % 12 || 12) : finalH, m: mStr, p };
    } else if (timeToParse) {
      const match = timeToParse.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (match) return { h: parseInt(match[1]), m: match[2], p: match[3].toUpperCase() };
    }
    return { h: 10, m: "00", p: "AM" };
  };

  const initial = getInitialTime(type);
  const [hour, setHour] = useState(initial.h);
  const [minute, setMinute] = useState(initial.m);
  const [period, setPeriod] = useState(initial.p);

  const nowTime = new Date();
  const currentHour24 = nowTime.getHours();
  const currentMinute = nowTime.getMinutes();
  const isTimeToday = activeTab === 'start' && selectedDate && selectedDate.toDateString() === nowTime.toDateString();
  const isAMDisabled = isTimeToday && currentHour24 >= 12;

  const getHour24 = (h, p) => {
    if (p === 'PM') {
      return h === 12 ? 12 : h + 12;
    } else {
      return h === 12 ? 0 : h;
    }
  };

  const isHourDisabled = (h) => {
    if (!isTimeToday) return false;
    return getHour24(h, period) < currentHour24;
  };

  const isMinuteDisabled = (mStr) => {
    if (!isTimeToday) return false;
    const m = parseInt(mStr);
    const selectedHour24 = getHour24(hour, period);
    if (selectedHour24 < currentHour24) return true;
    if (selectedHour24 === currentHour24) {
      return m < currentMinute;
    }
    return false;
  };

  const dateScrollerRef = useRef(null);
  const hourScrollerRef = useRef(null);
  const minuteScrollerRef = useRef(null);
  const periodScrollerRef = useRef(null);

  const scrollTimeouts = useRef({ date: null, hour: null, minute: null, period: null });

  const scrollToActiveElement = (ref) => {
    if (ref.current) {
      const active = ref.current.querySelector('.font-bold');
      if (active) {
        const containerHeight = ref.current.offsetHeight;
        const activeTop = active.offsetTop;
        const activeHeight = active.offsetHeight;
        ref.current.scrollTo({
          top: activeTop - (containerHeight / 2) + (activeHeight / 2),
          behavior: 'smooth'
        });
      }
    }
  };

  // Auto-correct if selected time is in the past for today
  useEffect(() => {
    if (!isOpen || !isTimeToday) return;

    const currentH24 = new Date().getHours();
    const currentM = new Date().getMinutes();
    const selectedH24 = getHour24(hour, period);
    const selectedM = parseInt(minute);

    if (selectedH24 < currentH24 || (selectedH24 === currentH24 && selectedM < currentM)) {
      const now = new Date();
      let h24 = now.getHours();
      let m = Math.ceil(now.getMinutes() / 5) * 5;
      if (m >= 60) {
        m = 0;
        h24 = (h24 + 1) % 24;
      }
      const p = h24 >= 12 ? 'PM' : 'AM';
      let h12 = h24 % 12;
      h12 = h12 === 0 ? 12 : h12;
      const mStr = m.toString().padStart(2, '0');

      setHour(h12);
      setMinute(mStr);
      setPeriod(p);

      // Scroll the containers to center these new values
      setTimeout(() => {
        scrollToActiveElement(hourScrollerRef);
        scrollToActiveElement(minuteScrollerRef);
        scrollToActiveElement(periodScrollerRef);
      }, 100);
    }
  }, [hour, minute, period, selectedDate, isOpen, isTimeToday]);

  // Auto-scroll to active values when sheet opens or tab changes
  useEffect(() => {
    if (isOpen) {
      const scrollTimer = setTimeout(() => {
        const scrollToActive = (ref) => {
          if (ref.current) {
            const active = ref.current.querySelector('.font-bold');
            if (active) {
              const containerHeight = ref.current.offsetHeight;
              const activeTop = active.offsetTop;
              const activeHeight = active.offsetHeight;
              ref.current.scrollTo({
                top: activeTop - (containerHeight / 2) + (activeHeight / 2),
                behavior: 'smooth'
              });
            }
          }
        };

        scrollToActive(dateScrollerRef);
        if (activeTab === 'start') {
          scrollToActive(hourScrollerRef);
          scrollToActive(minuteScrollerRef);
          scrollToActive(periodScrollerRef);
        }
      }, 300); // Wait for DOM
      return () => clearTimeout(scrollTimer);
    }
  }, [isOpen, activeTab]);

  const dates = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const startDate = activeTab === 'end' ? new Date(pickupDate) : now;
  startDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 30; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push(d);
  }

  // If currently selectedDate is before our new calculated startDate, reset it
  useEffect(() => {
    if (isOpen && selectedDate < startDate) {
      setSelectedDate(startDate);
    }
  }, [startDate, selectedDate, isOpen]);

  // Sync state if tab changes or when opening
  useEffect(() => {
    if (!isOpen) return;

    const isStart = activeTab === 'start';
    const timeToParse = isStart ? pickupTime : returnTime;
    const dateToCheck = isStart ? pickupDate : returnDate;
    const now = new Date();
    const isToday = dateToCheck.toDateString() === now.toDateString();
    
    setSelectedDate(dateToCheck);

    if (isStart && (isToday || !timeToParse)) {
      let h24 = now.getHours();
      let m = Math.round(now.getMinutes() / 5) * 5;
      
      if (m >= 60) {
        m = 0;
        h24 = (h24 + 1) % 24;
      }
      
      const p = h24 >= 12 ? "PM" : "AM";
      let h12 = h24 % 12;
      h12 = h12 === 0 ? 12 : h12;
      const mStr = m.toString().padStart(2, '0');
      
      setHour(h12);
      setMinute(mStr);
      setPeriod(p);
    } else if (timeToParse) {
      const match = timeToParse.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (match) {
        setHour(parseInt(match[1]));
        setMinute(match[2]);
        setPeriod(match[3].toUpperCase());
      }
    } else {
      setHour(10);
      setMinute("00");
      setPeriod("AM");
    }
  }, [activeTab, pickupDate, returnDate, pickupTime, returnTime, isOpen]);

  // Handle scroll snap selecting
  const handleScrollDebounced = (ref, items, value, setValue, key) => {
    if (scrollTimeouts.current[key]) {
      clearTimeout(scrollTimeouts.current[key]);
    }
    
    scrollTimeouts.current[key] = setTimeout(() => {
      if (!ref.current) return;
      const container = ref.current;
      const containerCenter = container.scrollTop + container.offsetHeight / 2;
      const children = Array.from(container.children);
      const itemChildren = children.slice(1, children.length - 1);
      
      let closestVal = null;
      let minDistance = Infinity;
      
      itemChildren.forEach((child, index) => {
        const childCenter = child.offsetTop + child.offsetHeight / 2;
        const distance = Math.abs(containerCenter - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestVal = items[index];
        }
      });
      
      if (closestVal !== null && closestVal !== undefined) {
        if (key === 'date') {
          if (closestVal.toDateString() !== value.toDateString()) {
            setValue(closestVal);
          }
        } else {
          if (closestVal !== value) {
            setValue(closestVal);
          }
        }
      }
    }, 80);
  };



  const selectDate = (d) => {
    setSelectedDate(d);
    setTimeout(() => scrollToActiveElement(dateScrollerRef), 50);
  };

  const selectHour = (h) => {
    setHour(h);
    setTimeout(() => scrollToActiveElement(hourScrollerRef), 50);
  };

  const selectMinute = (m) => {
    setMinute(m);
    setTimeout(() => scrollToActiveElement(minuteScrollerRef), 50);
  };

  const selectPeriod = (p) => {
    setPeriod(p);
    setTimeout(() => scrollToActiveElement(periodScrollerRef), 50);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex flex-col justify-end bg-black/50 md:hidden">
      <div className="bg-white rounded-t-2xl flex flex-col max-h-[85vh] animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">
            {activeTab === 'start' ? 'Select trip start date & time' : 'Select trip end date'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Scrollers */}
        <div className="flex-1 overflow-hidden p-4">
          <div className="flex justify-between h-48 relative">
            {/* Center indicators */}
            <div className="absolute top-1/2 left-0 right-0 h-10 border-y border-gray-200 -translate-y-1/2 pointer-events-none"></div>

            {/* Date Scroller */}
            <div 
              ref={dateScrollerRef} 
              className={`overflow-y-auto scroll-snap-y text-center no-scrollbar transition-all duration-300 ${activeTab === 'start' ? 'flex-1' : 'w-full'}`}
              onScroll={() => handleScrollDebounced(dateScrollerRef, dates, selectedDate, setSelectedDate, 'date')}
            >
              <div className="h-20"></div>
              {dates.map((d, i) => {
                const isRealToday = d.toDateString() === new Date().toDateString();
                const isSelected = d.toDateString() === selectedDate.toDateString();
                return (
                  <div
                    key={i}
                    className={`py-2 text-base scroll-snap-align-center transition-all cursor-pointer ${isSelected ? 'text-black font-bold text-lg' : 'text-gray-400'}`}
                    onClick={() => selectDate(d)}
                  >
                    {isRealToday ? 'Today' : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                  </div>
                );
              })}
              <div className="h-20"></div>
            </div>

            {activeTab === 'start' && (
              <>
                {/* Hour Scroller */}
                <div 
                  ref={hourScrollerRef} 
                  className="w-16 overflow-y-auto scroll-snap-y text-center no-scrollbar"
                  onScroll={() => handleScrollDebounced(hourScrollerRef, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], hour, setHour, 'hour')}
                >
                  <div className="h-20"></div>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(h => {
                    const isDisabled = isHourDisabled(h);
                    return (
                      <div 
                        key={h} 
                        className={`py-2 text-lg transition-all cursor-pointer ${isDisabled ? 'opacity-30 line-through pointer-events-none text-gray-300' : (hour === h ? 'text-black font-bold' : 'text-gray-400')}`} 
                        onClick={() => !isDisabled && selectHour(h)}
                      >
                        {h}
                      </div>
                    );
                  })}
                  <div className="h-20"></div>
                </div>

                {/* Minute Scroller */}
                <div 
                  ref={minuteScrollerRef} 
                  className="w-16 overflow-y-auto scroll-snap-y text-center no-scrollbar"
                  onScroll={() => handleScrollDebounced(minuteScrollerRef, ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"], minute, setMinute, 'minute')}
                >
                  <div className="h-20"></div>
                  {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map(m => {
                    const isDisabled = isMinuteDisabled(m);
                    return (
                      <div 
                        key={m} 
                        className={`py-2 text-lg transition-all cursor-pointer ${isDisabled ? 'opacity-30 line-through pointer-events-none text-gray-300' : (minute === m ? 'text-black font-bold' : 'text-gray-400')}`} 
                        onClick={() => !isDisabled && selectMinute(m)}
                      >
                        {m}
                      </div>
                    );
                  })}
                  <div className="h-20"></div>
                </div>

                {/* Period Scroller */}
                <div 
                  ref={periodScrollerRef} 
                  className="w-16 overflow-y-auto scroll-snap-y text-center no-scrollbar"
                  onScroll={() => handleScrollDebounced(periodScrollerRef, ["AM", "PM"], period, setPeriod, 'period')}
                >
                  <div className="h-20"></div>
                  {["AM", "PM"].map(p => {
                    const isDisabled = p === 'AM' && isAMDisabled;
                    return (
                      <div 
                        key={p} 
                        className={`py-2 text-lg transition-all cursor-pointer ${isDisabled ? 'opacity-30 line-through pointer-events-none text-gray-300' : (period === p ? 'text-black font-bold' : 'text-gray-400')}`} 
                        onClick={() => !isDisabled && selectPeriod(p)}
                      >
                        {p}
                      </div>
                    );
                  })}
                  <div className="h-20"></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Advance booking indicator */}
        <div className="text-center text-xs text-gray-400 font-semibold mb-4 flex items-center justify-center gap-1">
          <span className="text-[14px]">ⓘ</span> Book up to 180 days in advance
        </div>

        {/* Footer Tabs / Trip Cards */}
        <div className="grid grid-cols-2 gap-4 px-4 mb-4" style={{ overflow: 'visible' }}>
          {/* Trip Start Card */}
          <div 
            className={`p-3 rounded-lg border cursor-pointer transition-all flex flex-col ${activeTab === 'start' ? (corporateMember ? 'border-2 border-[#F56753] bg-red-50/10' : 'border-2 border-blue-500 bg-blue-50/10') : 'border border-gray-200 bg-white'} ${journeyType === 'local' ? 'col-span-2 w-full text-center items-center justify-center' : 'justify-between'}`} 
            onClick={() => setActiveTab('start')}
          >
            <div className={journeyType === 'local' ? 'flex flex-col items-center text-center' : ''}>
              <div className={`text-[10px] font-bold ${corporateMember ? 'text-[#F56753]' : 'text-blue-500'} uppercase tracking-wide leading-none mb-1`}>TRIP START</div>
              <div className="text-[13px] font-bold text-gray-900 leading-tight">
                {pickupDate.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })} {pickupDate.getFullYear()}
              </div>
              <div className="text-[12px] font-semibold text-gray-600 mt-0.5">
                {pickupTime || (pickupDate.toDateString() === new Date().toDateString() ? (() => { const n=new Date(); const h=n.getHours()%12||12; const m=n.getMinutes().toString().padStart(2,'0'); const p=n.getHours()>=12?'PM':'AM'; return `${h}:${m} ${p}`; })() : "10:00 AM")}
              </div>
            </div>
          </div>

          {/* Trip End Card (Dynamic based on localTripType) - Only rendered for outstation */}
          {journeyType !== 'local' && (
            localTripType === 'oneWay' ? (
              /* + Trip End Card */
              <div 
                className="bg-gray-50 p-3 rounded-lg border border-dashed border-gray-300 cursor-pointer flex flex-col justify-center"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setLocalTripType('roundTrip');
                  setActiveTab('end');
                }}
              >
                <div className={`text-[13px] font-extrabold ${corporateMember ? 'text-[#F56753]' : 'text-blue-600'} mb-0.5`}>+ TRIP END</div>
                <div className="text-[10px] text-gray-500 font-semibold leading-tight">
                  Save more on round trips!
                </div>
              </div>
            ) : (
              /* Trip End Card with Close (x) Button */
              <div 
                className={`p-3 rounded-lg border cursor-pointer relative flex flex-col justify-between transition-all ${activeTab === 'end' ? (corporateMember ? 'border-2 border-[#F56753] bg-red-50/10' : 'border-2 border-blue-500 bg-blue-50/10') : 'border border-gray-200 bg-white'}`} 
                onClick={() => setActiveTab('end')}
              >
                {/* Close Button */}
                <button
                  type="button"
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-30"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocalTripType('oneWay');
                    setActiveTab('start');
                  }}
                >
                  <FaTimes className="text-gray-500 text-[10px]" />
                </button>

                <div>
                  <div className={`text-[10px] font-bold ${corporateMember ? 'text-[#F56753]' : 'text-blue-500'} uppercase tracking-wide leading-none mb-1`}>TRIP END</div>
                  <div className="text-[13px] font-bold text-gray-900 leading-tight">
                    {returnDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {returnDate.getFullYear()}
                  </div>
                  <div className="text-[11px] font-bold text-gray-500 mt-0.5">
                    {returnDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        {/* Done Button */}
        <div className="p-4 pt-0">
          <button
            className={`w-full text-white font-bold py-3 rounded-lg text-lg shadow-lg active:scale-[0.98] transition-transform ${corporateMember ? 'bg-[#F56753] hover:bg-[#D94E3A]' : 'bg-blue-600 hover:bg-blue-700'}`}
            onClick={() => onDone({ date: selectedDate, hour, minute, period, type: activeTab, localTripType })}
          >
            CONFIRM
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HeroSection;
